import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    NgZone,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { saveAs } from 'file-saver';
import { combineLatest, concat, from, merge, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, take, tap } from 'rxjs/operators';
import { Role } from 'src/app/classes/auth/role.class';
import { SelectExecutorDialogComponent } from 'src/app/select-executor-dialog/select-executor-dialog.component';
import { AfasService } from 'src/app/services/afas.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { PlanningDetailDialogService } from 'src/app/services/dialog/planning-detail-dialog.service';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { EntityId, EntityType } from 'src/app/services/entities/entity-type.class';
import { LeaveService } from 'src/app/services/leave.service';
import { FormattedAddress } from 'src/app/services/location.service';
import { PlanningHasService } from 'src/app/services/planning/planning-has.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { UserPlanningService } from 'src/app/services/planning/user-planning.service';
import { ProjectService } from 'src/app/services/project.service';
import { PersonnelPlanningSettings, USER_SETTINGS_MODULE_KEYS } from 'src/app/services/user/user-settings';
import { UserService } from 'src/app/services/user/user.service';
import { Settings } from 'src/app/settings.class';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { environment } from '../../../environments/environment';
import { Entity } from '../../classes/entity.class';
import { Leave } from '../../classes/leave';
import { PlanningHasEntity } from '../../classes/planning-has-entity.class';
import { Planning } from '../../classes/planning.class';
import { Project } from '../../classes/project.class';
import { RealisationHourtype } from '../../classes/realisation';
import { UserPlanning } from '../../classes/user-planning';
import { User } from '../../classes/user.class';
import { CodaltComponent } from '../../codalt.component';
import { EmployeeService } from '../../services/employee.service';
import { ProjectDetailDialogComponent } from '../project-detail-dialog/project-detail-dialog.component';
import { AddLeaveNameDialogComponent, returnData } from './add-leave-name-dialog/add-leave-name-dialog.component';
import { AddProjectDialogComponent } from './add-project-dialog/add-project-dialog.component';
import { EmployeeDayInfoDialogComponent } from './employee-day-info-dialog/employee-day-info-dialog.component';
import { EmployeeIsNightPipe } from './employee-is-night.pipe';
import { EmployeePlanningMakeKeyPipe } from './employee-planning-make-key.pipe';
import { EmployeePlanningUserRowComponent } from './employee-planning-user-row/employee-planning-user-row.component';

@Component({
    selector: 'app-employee-planning',
    templateUrl: './employee-planning.component.html',
    styleUrls: ['./employee-planning.component.scss'],
    providers: [EmployeeIsNightPipe, EmployeePlanningMakeKeyPipe],
    standalone: false,
})
export class EmployeePlanningComponent extends CodaltComponent implements OnInit, AfterViewInit, OnDestroy {
    private static readonly planningHoverCellSelector = 'td.day[data-day], th.day[data-day]';
    private static readonly planningHighlightHeaderSelector = 'th.day';

    private cleanupPlanningPointerOver?: () => void;
    private highlightedCells: HTMLElement[] = [];
    private highlightedKey?: string;
    private planningTable?: HTMLTableElement;
    private viewInitialized = false;
    private projectIdsWithMultipleRows = new Set<string>();
    // Empty rows are removed once after the initial load. From then on rows are only added, never hidden again.
    private initialProjectDaysFiltered = false;

    readonly RealisationHourtype = RealisationHourtype;
    showHelp = false;

    fcNightPlanning = new FormControl();

    fcProjectIds = new FormControl<string[]>();
    fcPerformerIds = new FormControl<number[]>();
    fcRoleKeys = new FormControl<string[]>(
        LocalStorage.getUser()?.settings?.modules?.[USER_SETTINGS_MODULE_KEYS.PERSONNEL_PLANNING]?.uniqueRoleKeys ?? [],
        { nonNullable: true }
    );
    userDatesMap = new Map<number, string[]>();
    @ViewChild('planningTable') planningTableRef?: ElementRef<HTMLTableElement>;
    @ViewChildren(EmployeePlanningUserRowComponent)
    private employeePlanningUserRows: QueryList<EmployeePlanningUserRowComponent>;

    projects: Project[];
    planningList: Planning[];
    usersMap: Map<number, User>;
    entitiesMap: Map<number, Entity>;
    entityTypeMap: Map<string, EntityType>;
    entities: Entity[];
    leaveData: Leave[];
    leaveList: Leave[];
    asfaltploegenModus = false;

    print = false;
    fromDate: Date;
    toDate: Date;
    disableNext = false;
    week: number;
    year: number;

    EntityId = EntityId;
    downloadingPdf = false;

    projectDaysList: ProjectDay[];
    projectDaysListFiltered: ProjectDay[];

    smallMaterial: Entity[];

    addingHiringForEntityId: number;
    entityHiringNames = new Map<number, string[]>();
    hireEntities: Entity[];
    fcAddHiring = new FormControl<string>();
    @ViewChild('addHiringInp') addHiringInput: ElementRef;
    entityWeekUsedDays: Map<string, string[]> = new Map<string, string[]>();
    hiringAutocompleteOptions: string[];
    uniqueProjectsIds: string[];

    users: User[];
    uniqueRoles: Array<Role & { users: User[] }>;
    filteredUniqueRoles: Array<Role & { users: User[] }>;
    teams: {
        entityType: EntityType;
        entity: Entity;
        users: User[];
        showUsers: boolean;
    }[];

    projectManagers: User[];
    executors: User[];
    filteredExecutors: User[];

    dateHasUserMap = new Map<string, boolean>();
    projectDayTeamMap: Map<string, Planning> = new Map<string, Planning>();

    projectDayMaterialMap: Map<string, PlanningHasEntity[]> = new Map<string, PlanningHasEntity[]>();

    dayUserMap: Map<string, string> = new Map<string, string>();
    userDayHasNightMap = new Map<string, boolean>();
    userDayConflictMap = new Map<string, boolean>();

    projectDayUserMap: Map<string, ProjectDayUserState> = new Map<string, ProjectDayUserState>();
    userWeekCount: Map<number, number> = new Map<number, number>();
    blockedMap: Map<string, { icon: string; description: string; startDate: Date; endDate: Date }>;

    allUsers: User[];
    leaveDays: { day: Date; dayLetter: string; dayKey: string; dayLabel: string; cssClass: string }[] = [];
    leaveDayUserMap: Map<string, Leave> = new Map<string, Leave>();
    afasAbsenceUserMap: Map<string, boolean> = new Map<string, boolean>();
    currentHourType: RealisationHourtype;
    contextUser: User;
    contextDay: Date;
    contextComment = 'Personeelsplanning';
    contextProject: Project;

    leavesToSave: Leave[] = [];
    planningsToSave: Planning[] = [];
    private projectDayRowCounter = 0;
    projectDayPerformerChangesToSave: ProjectDayPerformerChange[] = [];
    userPlanningsToSave: UserPlanning[] = [];
    planningHasToSave: PlanningHasEntity[] = [];
    formDirty = false;
    saving = false;
    rollingBack = false;
    userPlannings: UserPlanning[];
    planningHass: PlanningHasEntity[];
    periodSubscriptions: Subscription;

    PlanningStatus = this.environment.status.Planning;
    private personnelPlanningSettings: PersonnelPlanningSettings = {};
    dataLoadInProgress = false;

    readonly = !AuthorisationService.hasFunctionality(Functionalities.EmployeePlanningManagement);
    @ViewChild('picker') picker: MatDatepicker<Date>;

    constructor(
        private confirmDialogService: ConfirmDialogService,
        private planningService: PlanningService,
        private projectService: ProjectService,
        private activatedRoute: ActivatedRoute,
        private userPlanningService: UserPlanningService,
        private afasService: AfasService,
        private planningDetailDialogService: PlanningDetailDialogService,
        private entitiesService: EntitiesService,
        private router: Router,
        private dialog: MatDialog,
        private confirmDialog: ConfirmDialogService,
        private authService: AuthService,
        private authorisationService: AuthorisationService,
        private userService: UserService,
        private planningHasService: PlanningHasService,
        private ngZone: NgZone,
        private leaveService: LeaveService,
        protected employeeService: EmployeeService,
        private employeeIsNightPipe: EmployeeIsNightPipe,
        private makeKeyPipe: EmployeePlanningMakeKeyPipe
    ) {
        super();
    }

    private filterProjectDaysList(): void {
        if (!this.projectDaysList) {
            this.projectDaysListFiltered = [];
            this.filteredExecutors = [];
            this.projectIdsWithMultipleRows.clear();
            return;
        }

        const projectIds = this.fcProjectIds.value ?? [];
        const performerIds = this.fcPerformerIds.value ?? [];
        const uniqueRoleKeys = this.fcRoleKeys.value ?? [];

        // Filter the initial rows by actual personnel-planning data. Later refreshes only apply the user-selected
        // project and performer filters, so manually added and websocket-added rows remain visible.
        if (!this.initialProjectDaysFiltered && this.projectDaysList.length) {
            const projectIdsWithMultipleRows = new Set(
                this.projectDaysList
                    .filter(
                        (projectDays, index, rows) =>
                            rows.findIndex(row => row.projectId === projectDays.projectId) !== index
                    )
                    .map(projectDays => projectDays.projectId)
            );
            const { projectIds: projectIdsWithPlanning, rowKeys } = this.getUserPlanningVisibility();
            this.projectDaysList = this.projectDaysList.filter(
                projectDays =>
                    projectIdsWithPlanning.has(projectDays.projectId) &&
                    (!projectIdsWithMultipleRows.has(projectDays.projectId) || rowKeys.has(projectDays.rowKey))
            );
            this.initialProjectDaysFiltered = true;
        }

        // Cache projects with variants once. Material-cell lookups use this to avoid scanning all project rows
        // during every change-detection pass while still preventing fallback into a different performer row.
        const encounteredProjectIds = new Set<string>();
        this.projectIdsWithMultipleRows = new Set<string>();
        this.projectDaysList.forEach(projectDays => {
            if (encounteredProjectIds.has(projectDays.projectId)) {
                this.projectIdsWithMultipleRows.add(projectDays.projectId);
            }
            encounteredProjectIds.add(projectDays.projectId);
        });

        const filteredProjectDaysList = this.projectDaysList
            .filter(pdl => {
                const performerId = pdl.form.controls.performer_id.value ?? pdl.project?.performer_id ?? null;
                return (
                    (!projectIds.length || projectIds.includes(pdl.projectId)) &&
                    (!performerIds.length || performerIds.includes(performerId))
                );
            })
            .sort((a, b) => a.projectId.localeCompare(b.projectId, undefined, { numeric: true }));
        const filteredProjectDaysChanged =
            this.projectDaysListFiltered?.length !== filteredProjectDaysList.length ||
            filteredProjectDaysList.some(
                (projectDays, index) => projectDays.rowKey !== this.projectDaysListFiltered[index]?.rowKey
            );
        if (filteredProjectDaysChanged) {
            // Preserve the input reference so OnPush employee rows stay skipped when visible projects did not change.
            this.projectDaysListFiltered = filteredProjectDaysList;
        }

        this.filteredExecutors = (this.executors ?? []).filter(e =>
            this.projectDaysListFiltered.some(
                pdl => (pdl.form.controls.performer_id.value ?? pdl.project?.performer_id) === e.id
            )
        );

        this.filteredUniqueRoles = (this.uniqueRoles ?? []).filter(
            role => !uniqueRoleKeys.length || uniqueRoleKeys.includes(role.key)
        );
    }

    ngOnInit(): void {
        this.personnelPlanningSettings =
            LocalStorage.getUser()?.settings?.modules?.[USER_SETTINGS_MODULE_KEYS.PERSONNEL_PLANNING] ?? {};
        this.subscriptions.add(
            // Refresh the cached current user from the server so this page always opens with the latest preferences.
            this.authService.getUserDetails().subscribe(response => {
                this.personnelPlanningSettings =
                    response.settings?.modules?.[USER_SETTINGS_MODULE_KEYS.PERSONNEL_PLANNING] ?? {};
                this.fcRoleKeys.setValue(this.personnelPlanningSettings.uniqueRoleKeys ?? [], { emitEvent: false });
                this.filterProjectDaysList();
            })
        );
        this.subscriptions.add(
            this.activatedRoute.queryParams.subscribe(q => {
                this.print = q.print === 'true';
            })
        );
        this.subscriptions.add(
            merge(this.fcPerformerIds.valueChanges, this.fcProjectIds.valueChanges, this.fcRoleKeys.valueChanges)
                .pipe(distinctUntilChanged())
                .pipe(debounceTime(100))
                .subscribe(() => {
                    this.filterProjectDaysList();
                })
        );
        this.subscriptions.add(
            this.fcRoleKeys.valueChanges.subscribe(roleKeys => {
                this.userService
                    .saveUserSettings(USER_SETTINGS_MODULE_KEYS.PERSONNEL_PLANNING, {
                        ...this.personnelPlanningSettings,
                        uniqueRoleKeys: roleKeys,
                    })
                    .subscribe(response => {
                        this.personnelPlanningSettings =
                            response.data.modules?.[USER_SETTINGS_MODULE_KEYS.PERSONNEL_PLANNING] ?? {};
                        const user = LocalStorage.getUser();
                        if (user) {
                            LocalStorage.setUser({
                                ...user,
                                settings: response.data,
                            });
                        }
                    });
            })
        );
        this.subscriptions.add(
            this.activatedRoute.params.subscribe(params => {
                this.dataLoadInProgress = true;
                this.clearMaps();
                const monday = new Date();
                if (params?.year) {
                    monday.setFullYear(params.year);
                }
                Utils.setTime(monday, 0, 0);
                const year = monday.getFullYear();
                monday.setMonth(0);
                monday.setDate(1);
                monday.setDate(monday.getDate() - ((monday.getDay() % 7) - 1));
                const beginDate = new Date(monday);

                const week = +(params.week ?? Utils.formatDate(new Date(), 'w'));
                beginDate.setDate(beginDate.getDate() + 7 * (week - 1));

                const endDate = Utils.setTime(new Date(beginDate), 23, 59);
                endDate.setDate(endDate.getDate() + 6);

                this.week = week;
                this.year = week > 1 ? beginDate.getFullYear() : endDate.getFullYear();

                this.fromDate = beginDate;
                this.toDate = endDate;

                if (this.readonly) {
                    const maxDate = new Date();
                    // When after thursday 15:00, next week pls
                    if (maxDate.getDay() > 4 || (maxDate.getDay() === 4 && maxDate.getHours() > 15)) {
                        maxDate.setDate(maxDate.getDate() + 7);
                    }
                    Utils.setTime(maxDate, 23, 59);
                    maxDate.setDate(maxDate.getDate() + (7 - maxDate.getDay()));
                    if (maxDate < this.toDate) {
                        this.prev();
                    } else {
                        this.getData();
                    }
                    this.disableNext = Utils.dateFormatEquals(maxDate, this.toDate, 'yyyy-MM-dd');
                } else {
                    this.getData();
                }

                this.leaveDays = [];
                const loopDate = new Date(this.fromDate);
                while (loopDate.getTime() < this.toDate.getTime()) {
                    const day = Utils.setTime(new Date(loopDate), 0, 0);
                    const dayLabel = Utils.formatDate(day, 'E d MMM y');
                    this.leaveDays.push({
                        day,
                        dayLetter: dayLabel.charAt(0),
                        dayKey: Utils.formatDate(day),
                        dayLabel,
                        cssClass: 'day day-' + dayLabel.charAt(0),
                    });
                    loopDate.setDate(loopDate.getDate() + 1);
                }
            })
        );
    }

    ngAfterViewInit(): void {
        this.viewInitialized = true;
        this.setupPlanningPointerOverListener();
    }

    ngOnDestroy() {
        this.teardownPlanningPointerOverListener();
        this.periodSubscriptions?.unsubscribe();
        super.ngOnDestroy();
    }

    @HostListener('window:beforeunload', ['$event'])
    unloadNotification($event: any) {
        if (
            this.planningsToSave?.length > 0 ||
            this.userPlanningsToSave?.length > 0 ||
            this.planningHasToSave?.length > 0 ||
            this.projectDayPerformerChangesToSave?.length > 0
        ) {
            $event.returnValue = true;
        }
    }

    openChangesBackActionCheck(): Promise<boolean> {
        return new Promise(resolve => {
            if (
                this.planningsToSave?.length > 0 ||
                this.userPlanningsToSave?.length > 0 ||
                this.planningHasToSave?.length > 0 ||
                this.projectDayPerformerChangesToSave?.length > 0
            ) {
                this.confirmDialog
                    .confirm({
                        title: 'Niet opgeslagen wijzigingen',
                        text: `Wilt u de niet opgeslagen wijzigingen verwerpen?`,
                        confirm: 'Hier blijven',
                        cancel: 'Wijzigingen verwerpen',
                    })
                    .then(
                        () => {
                            resolve(false);
                        },
                        () => {
                            this.userPlanningsToSave = [];
                            this.planningHasToSave = [];
                            this.planningsToSave = [];
                            this.projectDayPerformerChangesToSave = [];
                            resolve(true);
                        }
                    );
            } else {
                resolve(true);
            }
        });
    }

    makeKey(date: Date | string, ...parts: Array<string | number | null | undefined>) {
        return this.makeKeyPipe.transform(date, ...parts);
    }

    /**
     * Normalize the user planning mutations to save, so that we don't send unnecessary create+delete or duplicate updates to the server.
     */
    private normalizeUserPlanningsToSave(): void {
        const normalized: UserPlanning[] = [];

        for (const userPlanning of this.userPlanningsToSave) {
            // Unsaved create + delete cancels out.
            if (!userPlanning.id && userPlanning.deleted_at) {
                continue;
            }

            const existingMutation = userPlanning.id
                ? normalized.find(existing => existing.id === userPlanning.id)
                : normalized.find(
                      existing => !existing.id && this.isSameExactUserPlanningMutation(existing, userPlanning)
                  );

            if (existingMutation) {
                Object.assign(existingMutation, userPlanning);

                if (!existingMutation.id && existingMutation.deleted_at) {
                    normalized.splice(normalized.indexOf(existingMutation), 1);
                }

                continue;
            }

            const inverseMutation = normalized.find(
                existing =>
                    this.isSameExactUserPlanningMutation(existing, userPlanning) &&
                    !!existing.deleted_at !== !!userPlanning.deleted_at
            );

            if (inverseMutation) {
                normalized.splice(normalized.indexOf(inverseMutation), 1);
                continue;
            }

            normalized.push(userPlanning);
        }

        this.userPlanningsToSave = normalized;
    }

    private isSameUserPlanningTimeSlot(a: UserPlanning, b: UserPlanning): boolean {
        return (
            a.user_id === b.user_id &&
            Utils.getTimeOrNull(a.begindate) === Utils.getTimeOrNull(b.begindate) &&
            Utils.getTimeOrNull(a.enddate) === Utils.getTimeOrNull(b.enddate)
        );
    }

    /**
     * Identity for a single user-planning mutation without using planning_id.
     * This keeps delete+re-add on the same visible cell cancellable, while allowing
     * a delete on one project and a create on another project for the same time slot.
     */
    private isSameExactUserPlanningMutation(a: UserPlanning, b: UserPlanning): boolean {
        return (
            this.isSameUserPlanningTimeSlot(a, b) &&
            (a.afas_project_id ?? null) === (b.afas_project_id ?? null) &&
            (a.performer_id ?? null) === (b.performer_id ?? null)
        );
    }

    private findActiveExistingUserPlanning(candidate: UserPlanning): UserPlanning | undefined {
        const ignoredDeletedIds = new Set(
            (this.userPlanningsToSave ?? [])
                .filter(userPlanning => !!userPlanning.id && !!userPlanning.deleted_at)
                .map(userPlanning => userPlanning.id) as number[]
        );

        return this.userPlannings?.find(existing => {
            if (!existing.id || existing.deleted_at || ignoredDeletedIds.has(existing.id)) {
                return false;
            }

            return (
                existing.user_id === candidate.user_id &&
                Utils.getTimeOrNull(existing.begindate) === Utils.getTimeOrNull(candidate.begindate) &&
                Utils.getTimeOrNull(existing.enddate) === Utils.getTimeOrNull(candidate.enddate) &&
                (existing.afas_project_id ?? null) === (candidate.afas_project_id ?? null) &&
                (existing.performer_id ?? null) === (candidate.performer_id ?? null) &&
                (existing.planning_id ?? null) === (candidate.planning_id ?? null)
            );
        });
    }

    private showAlreadyPlannedOnDayDialog(subject: 'De persoon' | 'Deze ploeg'): void {
        this.confirmDialogService.confirm({
            title: 'Al ingepland op deze dag',
            text: `${subject} is op deze dag al ingepland en kan daarom alleen via de transport- of weekplanning ingepland worden.`,
            confirm: 'Sluiten',
            dialogOptions: {
                disableClose: false,
            },
        });
    }

    /**
     * Rebuild the single user/day lookup from the row-based planning maps.
     * Counters and badges still read from dayUserMap, so after edits on multiple project rows
     * we recalculate which project should represent that user on that day.
     */
    private syncDayUserMap(day: Date | string, userId?: number | null): void {
        if (userId == null) {
            return;
        }

        const dayUserKey = this.makeKey(day, userId);
        const prefix = `${dayUserKey}-`;
        const matchingEntries = [...this.projectDayUserMap.entries()].filter(
            ([key, projectDayUser]) =>
                key.startsWith(prefix) &&
                (projectDayUser.userPlanning?.some(userPlanning => !userPlanning.deleted_at) ||
                    projectDayUser.planningHas?.some(
                        planningHas => planningHas.driver_user_id === userId && !planningHas.deleted_at
                    ))
        );
        const matchingEntry =
            matchingEntries.find(([, projectDayUser]) => this.employeeIsNightPipe.transform(projectDayUser)) ??
            matchingEntries[0];

        if (!matchingEntry) {
            this.dayUserMap.delete(dayUserKey);
            return;
        }

        const matchingProjectId =
            matchingEntry[1].userPlanning?.find(
                userPlanning => !userPlanning.deleted_at && (userId == null || userPlanning.user_id === userId)
            )?.afas_project_id ??
            this.resolveProjectId(
                matchingEntry[1].userPlanning?.find(
                    userPlanning => !userPlanning.deleted_at && (userId == null || userPlanning.user_id === userId)
                )
            ) ??
            this.resolveProjectId(
                matchingEntry[1].planningHas?.find(
                    planningHas => !planningHas.deleted_at && (userId == null || planningHas.driver_user_id === userId)
                )
            ) ??
            matchingEntry[0].slice(prefix.length).split('::')[0];

        this.dayUserMap.set(dayUserKey, matchingProjectId);
    }

    /**
     * Picks the visible project row that should represent a performer.
     * The last matching row can be preferred for unsaved project-only planning so edits stay in the clicked column.
     */
    private getProjectDayForPerformer(
        projectId?: string,
        performerId?: number | null,
        preferLastMatchingRow = false
    ): ProjectDay | undefined {
        if (!projectId) {
            return undefined;
        }

        const projectRows = this.projectDaysList?.filter(projectDay => projectDay.projectId === projectId) ?? [];

        if (performerId != null) {
            const performerRows = projectRows.filter(
                projectDay => projectDay.form.controls.performer_id.value === performerId
            );

            if (performerRows.length) {
                return preferLastMatchingRow ? performerRows[performerRows.length - 1] : performerRows[0];
            }
        }

        return projectRows[0];
    }

    /**
     * Finds the performer that should own a material assignment in this view.
     * When the backend did not persist `performer_id`, we infer it from the driver's user planning on that day.
     */
    private resolvePlanningHasPerformerId(
        planningHas: PlanningHasEntity,
        projectId?: string,
        userPlannings: UserPlanning[] = this.userPlannings ?? []
    ): number | undefined {
        if (planningHas.performer_id != null) {
            return planningHas.performer_id;
        }

        if (!planningHas.driver_user_id || !projectId) {
            return undefined;
        }

        const planningDayKey = Utils.formatDate(planningHas.begindate);

        return userPlannings.find(
            userPlanning =>
                !userPlanning.deleted_at &&
                userPlanning.user_id === planningHas.driver_user_id &&
                userPlanning.performer_id != null &&
                this.resolveProjectId(userPlanning) === projectId &&
                Utils.formatDate(userPlanning.begindate) === planningDayKey
        )?.performer_id;
    }

    /**
     * Builds the possible material-map keys for one visible cell or, when requested, for every row of a project.
     * That keeps all material-specific row logic in one place instead of spreading row-key fallbacks around.
     */
    private getProjectDayMaterialMapKeys(
        day: Date | string,
        entityId: number,
        {
            projectId,
            rowKey,
            nonUseOnceName,
            performerId,
            preferLastMatchingRow = false,
            includeAllProjectRows = false,
        }: {
            projectId?: string;
            rowKey?: string;
            nonUseOnceName?: string | null;
            performerId?: number | null;
            preferLastMatchingRow?: boolean;
            includeAllProjectRows?: boolean;
        }
    ): string[] {
        const hasMultipleProjectRows =
            projectId != null &&
            (this.projectDaysList?.filter(projectDay => projectDay.projectId === projectId).length ?? 0) > 1;
        const identifiers = includeAllProjectRows
            ? [
                  rowKey,
                  this.getProjectDayForPerformer(projectId, performerId)?.rowKey ?? projectId,
                  ...(projectId != null
                      ? (this.projectDaysList
                            ?.filter(projectDay => projectDay.projectId === projectId)
                            .map(projectDay => projectDay.rowKey) ?? [])
                      : []),
                  projectId,
              ]
            : rowKey
              ? [rowKey, ...(hasMultipleProjectRows ? [] : [projectId])]
              : [this.getProjectDayForPerformer(projectId, performerId, preferLastMatchingRow)?.rowKey, projectId];

        return [...new Set(identifiers.filter(Boolean))].map(identifier =>
            this.makeKey(day, entityId, identifier, nonUseOnceName)
        );
    }

    /**
     * Returns the first material entry that belongs to the addressed row.
     * Project-level fallback is only safe when no performer variants exist.
     */
    getProjectDayMaterialEntry(
        day: Date | string,
        entityId: number,
        projectId?: string,
        rowKey?: string,
        nonUseOnceName?: string | null,
        performerId?: number | null,
        preferLastMatchingRow = false
    ): PlanningHasEntity[] | undefined {
        if (rowKey) {
            // Grid cells already know their row key. Direct lookups avoid rebuilding candidate-key arrays and
            // rescanning every project row for each of the thousands of material cells during change detection.
            const rowMaterial = this.projectDayMaterialMap.get(this.makeKey(day, entityId, rowKey, nonUseOnceName));
            if (rowMaterial?.length) {
                return rowMaterial;
            }
            if (!projectId || this.projectIdsWithMultipleRows.has(projectId)) {
                return undefined;
            }

            const projectMaterial = this.projectDayMaterialMap.get(
                this.makeKey(day, entityId, projectId, nonUseOnceName)
            );
            return projectMaterial?.length ? projectMaterial : undefined;
        }

        return this.getProjectDayMaterialMapKeys(day, entityId, {
            projectId,
            rowKey,
            nonUseOnceName,
            performerId,
            preferLastMatchingRow,
        })
            .map(materialKey => this.projectDayMaterialMap.get(materialKey))
            .find(material => !!material?.length);
    }

    /**
     * Removes one material assignment from every map slot where it may have been stored.
     * This is used before reassigning driver/performer links and while clearing saved mutations from the view.
     */
    private removePlanningHasMaterialMapEntry(
        planningHas: PlanningHasEntity,
        projectId?: string,
        rowKey?: string,
        performerId?: number | null
    ): void {
        const nonUseOnceName = this.entitiesMap.get(planningHas.entity_id)?.use_once ? null : planningHas.name;

        this.getProjectDayMaterialMapKeys(planningHas.begindate, planningHas.entity_id, {
            projectId,
            rowKey,
            nonUseOnceName,
            performerId,
            includeAllProjectRows: true,
        }).forEach(materialKey => {
            const materialEntries = this.projectDayMaterialMap.get(materialKey);

            if (!materialEntries?.length) {
                return;
            }

            const nextEntries = materialEntries.filter(
                existingPlanningHas =>
                    existingPlanningHas !== planningHas &&
                    (!planningHas.id || existingPlanningHas.id !== planningHas.id)
            );

            if (nextEntries.length) {
                this.projectDayMaterialMap.set(materialKey, nextEntries);
            } else {
                this.projectDayMaterialMap.delete(materialKey);
            }
        });
    }

    /**
     * Rewrites one material assignment into the correct row-specific map slot after its performer or driver changed.
     */
    private syncPlanningHasMaterialMapEntry(
        planningHas: PlanningHasEntity,
        projectId?: string,
        rowKey?: string,
        previousPerformerId?: number | null
    ): void {
        this.removePlanningHasMaterialMapEntry(planningHas, projectId, rowKey, previousPerformerId);

        if (planningHas.deleted_at) {
            return;
        }

        const nonUseOnceName = this.entitiesMap.get(planningHas.entity_id)?.use_once ? null : planningHas.name;
        const materialKey = this.getProjectDayMaterialMapKeys(planningHas.begindate, planningHas.entity_id, {
            projectId,
            rowKey,
            nonUseOnceName,
            performerId: planningHas.performer_id,
        })[0];

        if (!materialKey) {
            return;
        }

        const materialEntries = this.projectDayMaterialMap.get(materialKey) ?? [];
        if (
            !materialEntries.some(
                existingPlanningHas => existingPlanningHas === planningHas || existingPlanningHas.id === planningHas.id
            )
        ) {
            materialEntries.push(planningHas);
        }
        this.projectDayMaterialMap.set(materialKey, materialEntries);
    }

    private setupPlanningPointerOverListener() {
        if (!this.viewInitialized) {
            return;
        }

        const table = this.planningTableRef?.nativeElement;
        if (!table) {
            this.teardownPlanningPointerOverListener();
            return;
        }

        if (this.planningTable === table && this.cleanupPlanningPointerOver) {
            return;
        }

        this.cleanupPlanningPointerOver?.();
        this.clearPlanningHighlight();
        this.planningTable = table;

        this.ngZone.runOutsideAngular(() => {
            const handlePointerOver = (event: PointerEvent) => this.handlePlanningPointerOver(event);
            const handlePointerLeave = () => this.clearPlanningHighlight();

            table.addEventListener('pointerover', handlePointerOver, { passive: true });
            table.addEventListener('pointerleave', handlePointerLeave, { passive: true });
            this.cleanupPlanningPointerOver = () => {
                table.removeEventListener('pointerover', handlePointerOver);
                table.removeEventListener('pointerleave', handlePointerLeave);
            };
        });
    }

    private teardownPlanningPointerOverListener() {
        this.cleanupPlanningPointerOver?.();
        this.cleanupPlanningPointerOver = undefined;
        this.planningTable = undefined;
        this.clearPlanningHighlight();
    }

    private handlePlanningPointerOver(event: PointerEvent): void {
        const cell = (event.target as HTMLElement | null)?.closest<HTMLElement>(
            EmployeePlanningComponent.planningHoverCellSelector
        );

        if (!cell) {
            return;
        }

        this.setPlanningHighlight(cell.dataset['day'], cell.dataset['rowKey'] ?? '');
    }

    private setPlanningHighlight(day?: string, projectId = ''): void {
        if (!day) {
            this.clearPlanningHighlight();
            return;
        }

        const highlightKey = `${day}|${projectId}`;
        if (this.highlightedKey === highlightKey) {
            return;
        }

        const table = this.planningTableRef?.nativeElement;
        if (!table) {
            this.clearPlanningHighlight();
            return;
        }

        this.clearPlanningHighlight();

        const selector = `${EmployeePlanningComponent.planningHighlightHeaderSelector}[data-day="${day}"][data-row-key="${projectId}"]`;
        this.highlightedCells = Array.from(table.querySelectorAll<HTMLElement>(selector));
        this.highlightedCells.forEach(cell => cell.classList.add('highlight'));
        this.highlightedKey = highlightKey;
    }

    private clearPlanningHighlight(): void {
        this.highlightedCells.forEach(cell => cell.classList.remove('highlight'));
        this.highlightedCells = [];
        this.highlightedKey = undefined;
    }

    addEntityPlanning(
        event: Event,
        day: { day: Date; project?: Project },
        entity: Entity,
        nonUseOnceName?: string,
        silent?: boolean,
        rowKey?: string,
        performerId?: number
    ) {
        event.preventDefault();
        // ProjectDay also contains disabled cells whose project has not resolved yet; those cells cannot be saved.
        if (this.readonly || !day.project) {
            return;
        }

        const selectedPerformerId =
            performerId ??
            (rowKey
                ? this.projectDaysList?.find(projectDay => projectDay.rowKey === rowKey)?.form.controls.performer_id
                      .value
                : undefined);
        const key = this.getProjectDayMaterialMapKeys(day.day, entity.id, {
            projectId: day.project?.afas_project_id,
            rowKey,
            nonUseOnceName,
            performerId: selectedPerformerId,
        })[0];
        const planningHasList = this.getProjectDayMaterialEntry(
            day.day,
            entity.id,
            day.project?.afas_project_id,
            rowKey,
            nonUseOnceName,
            selectedPerformerId
        );
        const teamEntity = this.teams.find(team =>
            this.projectDayTeamMap.has(this.makeKey(day.day, team.entity.id, day.project?.afas_project_id))
        )?.entity;
        const planning = this.projectDayTeamMap.get(
            this.makeKey(day.day, teamEntity?.id, day.project?.afas_project_id)
        );

        if (planningHasList?.length) {
            if (planningHasList?.length > 1 && !Utils.pheSame(planningHasList[0], planningHasList[1])) {
                if (!silent) {
                    this.confirmDialogService.confirm({
                        title: 'Meer keren ingepland',
                        text: `Dit materieel is op deze dag meerdere keren ingepland en kan daarom alleen via de transport of weekplanning aangepast worden.`,
                        confirm: 'Sluiten',
                    });
                }
                return;
            } else if (!planningHasList[0].planning_id && !!planning && entity.entitytypes[0].visible_transport) {
                planningHasList[0].planning_id = planning.id;
                planningHasList[0].planning = planning;
                planningHasList[0].afas_project_id = undefined;
                if (!this.planningHasToSave.includes(planningHasList[0])) {
                    this.planningHasToSave.push(planningHasList[0]);
                }
            } else {
                if (nonUseOnceName) {
                    const dates = this.entityWeekUsedDays.get(nonUseOnceName + entity.id) ?? [];
                    dates.splice(dates.indexOf(Utils.formatDate(day.day)), 1);
                    this.entityWeekUsedDays.set(nonUseOnceName + entity.id, dates);
                }
                let reAddUserPlanningToUser = null;
                if (planningHasList[0].driver_user_id) {
                    const user = this.usersMap.get(planningHasList[0].driver_user_id);
                    // don't remove user, add as normal userPlanning
                    this.addUserPlanningProject(
                        event,
                        day,
                        this.getProjectDayEntry(
                            this.projectDayUserMap,
                            day.day,
                            user.id,
                            day.project?.afas_project_id,
                            rowKey
                        ),
                        user
                    );
                    reAddUserPlanningToUser = user;
                }
                planningHasList[0].deleted_at = new Date();

                if (!this.planningHasToSave.includes(planningHasList[0])) {
                    this.planningHasToSave.push(planningHasList[0]);
                }
                if (Utils.pheSame(planningHasList[0], planningHasList[1])) {
                    planningHasList[1].deleted_at = new Date();

                    if (!this.planningHasToSave.includes(planningHasList[1])) {
                        this.planningHasToSave.push(planningHasList[1]);
                    }
                }
                if (key) {
                    this.projectDayMaterialMap.delete(key);
                }
                if (reAddUserPlanningToUser) {
                    this.addUserPlanningProject(
                        event,
                        day,
                        this.getProjectDayEntry(
                            this.projectDayUserMap,
                            day.day,
                            reAddUserPlanningToUser.id,
                            day.project?.afas_project_id,
                            rowKey
                        ),
                        reAddUserPlanningToUser,
                        true
                    );
                }
            }
        } else {
            const projectsIds = [
                ...new Set([
                    ...this.projects.map(p => p.afas_project_id),
                    ...this.planningList.map(p => p.afas_project_id ?? p.worknumber),
                ]),
            ];
            if (
                projectsIds
                    .map(projectId =>
                        this.getProjectDayMaterialMapKeys(day.day, entity.id, {
                            projectId,
                            rowKey: projectId === day.project?.afas_project_id ? rowKey : undefined,
                            nonUseOnceName,
                            performerId: projectId === day.project?.afas_project_id ? selectedPerformerId : undefined,
                            includeAllProjectRows: true,
                        }).some(materialKey => this.projectDayMaterialMap.has(materialKey))
                    )
                    .find(p => p)
            ) {
                if (!silent) {
                    this.confirmDialogService.confirm({
                        title: 'Al ingepland op deze dag',
                        text: nonUseOnceName
                            ? `Deze persoon is al ingehuurd op deze dag. Dubbele inhuur is niet mogelijk.`
                            : `Het materieel is op deze dag al ingepland en kan daarom
                    alleen via de transport- of weekplanning ingepland worden.`,
                        confirm: 'Sluiten',
                    });
                }
                return;
            }

            const fromDate = planning ? new Date(Utils.mainPlanning(planning).begindate) : new Date(day.day);
            const toDate = planning ? new Date(Utils.mainPlanning(planning).enddate) : new Date(day.day);
            if (
                !planning ||
                (planning &&
                    this.fcNightPlanning.value &&
                    Math.abs(fromDate.getHours() - Settings.DEFAULT_START_NIGHT) > 3)
            ) {
                const startTime =
                    60 * (this.fcNightPlanning.value ? Settings.DEFAULT_START_NIGHT : Settings.DEFAULT_START);
                fromDate.setMinutes(startTime);
                toDate.setMinutes(startTime);
                toDate.setMinutes(toDate.getMinutes() + 60 * Settings.DEFAULT_DURATION_OTHER);
            }

            const planningHas = new PlanningHasEntity();
            planningHas.entity_id = entity.id;
            planningHas.entitytype_key = entity.entitytypes[0].key;
            planningHas.entitytype = entity.entitytypes[0];
            planningHas.begindate = fromDate;
            planningHas.enddate = toDate;
            if (planning && [EntityId.HiringEmployee, EntityId.HiringMaterials].indexOf(entity.id) !== -1) {
                planningHas.planning = planning;
                planningHas.planning_id = planning.id;
            } else {
                planningHas.afas_project_id = day.project.afas_project_id;
            }
            planningHas.name = nonUseOnceName;
            if (selectedPerformerId != null) {
                planningHas.performer_id = selectedPerformerId;
            }
            let currentMaterial = this.projectDayMaterialMap.get(key);
            if (!currentMaterial) {
                currentMaterial = [];
            }
            if (entity.driver_user_id) {
                const userKey = this.makeKey(
                    day.day,
                    entity.driver_user_id,
                    rowKey ??
                        this.getProjectDayForPerformer(day.project?.afas_project_id, selectedPerformerId)?.rowKey ??
                        day.project?.afas_project_id
                );
                const projectDayUser = this.projectDayUserMap.get(userKey);
                if (projectDayUser) {
                    // remove user planning
                    const user = this.usersMap.get(entity.driver_user_id);
                    this.addUserPlanningProject(
                        event,
                        day,
                        this.projectDayUserMap.get(userKey),
                        user,
                        false,
                        selectedPerformerId,
                        rowKey
                    );
                    this.assignDriverToMaterialPlanning(day, user, planningHas, rowKey, selectedPerformerId);
                }
            }

            currentMaterial.push(planningHas);
            if (key) {
                this.projectDayMaterialMap.set(key, currentMaterial);
            }
            this.planningHasToSave.push(planningHas);

            if (nonUseOnceName) {
                const dates = this.entityWeekUsedDays.get(nonUseOnceName + entity.id) ?? [];
                dates.push(Utils.formatDate(day.day));
                this.entityWeekUsedDays.set(nonUseOnceName + entity.id, dates);
            }

            this.refreshPlanningView();
        }
        if (entity.driver_user_id) {
            this.markEmployeePlanningUserRowsForCheck([entity.driver_user_id]);
        }
        return true;
    }

    addProjectDayVariant(sourceProjectDays: ProjectDay, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();

        if (this.readonly) {
            return;
        }

        const sourceExecutorId = sourceProjectDays.form.controls.performer_id.value;
        if (!sourceExecutorId) {
            this.confirmDialogService.confirm({
                title: 'Uitvoerder ontbreekt',
                text: `Selecteer eerst een uitvoerder voor project ${sourceProjectDays.projectId}.`,
                confirm: 'Sluiten',
            });
            return;
        }

        const dialogData = {
            title: 'Extra inplanning toevoegen',
            message: `Kies een uitvoerder voor een extra inplanning op project ${sourceProjectDays.projectId}.`,
            confirmButtonText: 'Toevoegen',
            availableExecutors: this.executors,
            sourceExecutor: this.executors.find(e => e.id === sourceExecutorId),
        };

        const dialogRef = this.dialog.open(SelectExecutorDialogComponent, { data: dialogData });

        this.periodSubscriptions.add(
            dialogRef.afterClosed().subscribe(result => {
                if (!result?.selectedExecutor) {
                    return;
                }

                const projectDays = this.createProjectDays(
                    sourceProjectDays.projectId,
                    [sourceProjectDays.project].filter(p => !!p)
                );
                projectDays.form.patchValue(
                    {
                        location: sourceProjectDays.form.controls.location.value,
                        location_housenumber: sourceProjectDays.form.controls.location_housenumber.value,
                        location_place: sourceProjectDays.form.controls.location_place.value,
                        location_postcode: sourceProjectDays.form.controls.location_postcode.value,
                        location_straat: sourceProjectDays.form.controls.location_straat.value,
                        projectmanager_id: sourceProjectDays.form.controls.projectmanager_id.value,
                        performer_id: result.selectedExecutor.id,
                    },
                    { emitEvent: false }
                );
                projectDays.syncedPerformerId = result.selectedExecutor.id;

                const sourceIndex = this.projectDaysList.findIndex(pd => pd.rowKey === sourceProjectDays.rowKey);
                if (sourceIndex !== -1) {
                    this.projectDaysList.splice(sourceIndex + 1, 0, projectDays);
                } else {
                    this.projectDaysList.push(projectDays);
                }
                this.refreshPlanningView();
            })
        );
    }

    addProject() {
        const ref = this.dialog.open(AddProjectDialogComponent, {
            // Global dialog defaults omit autoFocus. Move focus before Material hides the page with aria-hidden.
            autoFocus: 'input',
            maxWidth: '400px',
            width: '100vw',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: { currentProjects: this.projectDaysListFiltered?.map(pdl => pdl.projectId) ?? [] },
        });
        this.periodSubscriptions.add(
            ref.afterClosed().subscribe(project => {
                if (project) {
                    // Hidden base rows are absent from the dialog's current-project list. Reuse one instead of
                    // creating a temporary duplicate that becomes visible after the websocket refresh.
                    const existingProjectDays = this.projectDaysList?.find(
                        projectDays =>
                            projectDays.projectId === project.afas_project_id &&
                            projectDays.form.controls.performer_id.value == null
                    );
                    const projectDays =
                        existingProjectDays ?? this.createProjectDays(project.afas_project_id, [project]);
                    if (!this.uniqueProjectsIds.includes(project.afas_project_id)) {
                        this.uniqueProjectsIds.push(project.afas_project_id);
                        this.uniqueProjectsIds = this.uniqueProjectsIds.sort((a, b) =>
                            a.localeCompare(b, undefined, { numeric: true })
                        );
                    }
                    if (!this.projectDaysList) {
                        this.projectDaysList = [];
                    }
                    if (!existingProjectDays) {
                        this.projectDaysList.splice(
                            this.uniqueProjectsIds.indexOf(project.afas_project_id),
                            0,
                            projectDays
                        );
                    }
                    this.refreshPlanningView();
                    requestAnimationFrame(() => this.setupPlanningPointerOverListener());
                }
            })
        );
    }

    addTeamPlanning(
        event,
        day: {
            day: Date;
            project?: Project;
        },
        team: {
            entityType: EntityType;
            entity: Entity;
            users: User[];
            showUsers: boolean;
        },
        rowKey?: string
    ) {
        event.preventDefault();
        const project = day.project;
        if (this.readonly || !project) {
            return;
        }
        const resolvedDay = { day: day.day, project };
        const key = this.makeKey(day.day, team.entity.id, day.project?.afas_project_id);
        let planning = this.projectDayTeamMap.get(key);
        if (planning && planning.user_planning?.length) {
            if (!planning?.id) {
                this.planningsToSave.splice(this.planningsToSave.indexOf(planning), 1);
                this.projectDayTeamMap.delete(key);
            }
            planning.user_planning.forEach(up => {
                const key = this.makeKey(day.day, up.user_id, rowKey ?? day.project?.afas_project_id);
                if (this.projectDayUserMap.has(key)) {
                    this.projectDayUserMap.delete(key);
                    this.syncDayUserMap(day.day, up.user_id);
                    if (!planning?.id) {
                        this.userPlanningsToSave.splice(this.userPlanningsToSave.indexOf(up), 1);
                    } else {
                        up.deleted_at = new Date();
                        this.userPlanningsToSave.push(up);
                    }
                    this.normalizeUserPlanningsToSave();
                }
            });
            planning.user_planning = [];
        } else {
            const projectsIds = [
                ...new Set([
                    ...this.projects.map(p => p.afas_project_id),
                    ...this.planningList.map(p => p.afas_project_id ?? p.worknumber),
                ]),
            ];
            if (!planning) {
                if (
                    projectsIds
                        .map(
                            projectId =>
                                !!this.projectDayTeamMap.get(this.makeKey(day.day, team.entity.id, projectId))
                                    ?.mainPlanning?.entity?.use_once
                        )
                        .find(p => p)
                ) {
                    this.showAlreadyPlannedOnDayDialog('Deze ploeg');
                    return;
                }
                planning = this.createPlanning(team.entity, team.entityType, resolvedDay);
            }

            const begindate = new Date(Utils.mainPlanning(planning).begindate);
            const enddate = new Date(Utils.mainPlanning(planning).enddate);
            const allRowKeys = (this.projectDaysList ?? []).map(pd => pd.rowKey);
            const performerId = rowKey
                ? (this.projectDaysList.find(pd => pd.rowKey === rowKey)?.form.controls.performer_id.value ?? undefined)
                : undefined;

            team.users.forEach(user => {
                if (
                    !this.projectDayUserMap.has(
                        this.makeKey(day.day, user.id, rowKey ?? day.project?.afas_project_id)
                    ) &&
                    !projectsIds
                        .map(projectId => this.projectDayUserMap.has(this.makeKey(day.day, user.id, projectId)))
                        .find(p => p) &&
                    !allRowKeys.map(rk => this.projectDayUserMap.has(this.makeKey(day.day, user.id, rk))).find(p => p)
                ) {
                    this.addUserPlanningToPlanningTeam(
                        planning,
                        user,
                        begindate,
                        enddate,
                        resolvedDay,
                        rowKey,
                        performerId
                    );
                }
            });
        }
        this.refreshPlanningView();
        this.markEmployeePlanningUserRowsForCheck(team.users.map(user => user.id));
    }

    private createPlanning(entity: Entity, entityType: EntityType, day: { day: Date; project: Project }) {
        const planning = this.planningService.newPlanningByType(entityType.key);
        const mainPlanning = this.planningService.getMainPlanning(planning);
        const fromDate = new Date(day.day);
        const toDate = new Date(day.day);
        const startTime = 60 * (this.fcNightPlanning.value ? Settings.DEFAULT_START_NIGHT : Settings.DEFAULT_START);
        fromDate.setMinutes(startTime);
        toDate.setMinutes(startTime);
        toDate.setMinutes(toDate.getMinutes() + 60 * Settings.DEFAULT_DURATION_TEAM);
        mainPlanning.begindate = fromDate;
        mainPlanning.enddate = toDate;
        mainPlanning.entity = entity;
        mainPlanning.entity_id = entity.id;
        planning.afas_project_id = day.project.afas_project_id;
        planning.worknumber = day.project.afas_project_id;
        planning.location = day.project.location;
        planning.performer = day.project.performer_id;
        planning.projectmanager = day.project.projectmanager_id;
        planning.asphalt_performer = day.project.asphalt_performer_id;
        planning.contractor = day.project.contractor;
        planning.status_key = this.PlanningStatus.PlanningDefinitief;
        planning.user_planning = [];
        this.planningsToSave.push(planning);
        const key = this.makeKey(day.day, entity.id, day.project?.afas_project_id);
        this.projectDayTeamMap.set(key, planning);
        return planning;
    }

    private addUserPlanningToPlanningTeam(
        planning: Planning,
        user: User,
        begindate: Date,
        enddate: Date,
        day: { day: Date; project: Project },
        rowKey?: string,
        performerId?: number
    ) {
        const userPlanning = new UserPlanning();
        userPlanning.planning_id = planning.id;
        userPlanning.planning = planning;
        userPlanning.afas_project_id = day.project?.afas_project_id;
        userPlanning.user_id = user.id;
        userPlanning.begindate = begindate;
        userPlanning.enddate = enddate;
        if (performerId) {
            userPlanning.performer_id = performerId;
        }

        if (this.userHasPlanningOnDay(day.day, user.id, day.project?.afas_project_id, rowKey, begindate, enddate)) {
            return;
        }

        this.storeUserPlanningOnProjectDay(userPlanning, day, user.id, 'userPlanning', rowKey, planning);
        this.refreshPlanningView();
    }

    public getTeamCountForDay(dayKey: string, team: { entity: Entity; users: User[] }, rowKey: string) {
        const count = team.users.filter(u => this.projectDayUserMap.has(this.makeKey(dayKey, u.id, rowKey))).length;
        return count || '';
    }

    /**
     * Queues removal of a single user planning and removes its current row entry from the view maps.
     * Optionally removes it from the linked planning object as well.
     */
    private removeSingleUserPlanningFromProjectDay(
        day: Date,
        userId: number,
        projectId?: string,
        rowKey?: string,
        projectDayUser?: ProjectDayUserState,
        planning?: Planning
    ): void {
        const userPlanning = projectDayUser?.userPlanning?.[0];
        if (!userPlanning) {
            return;
        }

        userPlanning.deleted_at = new Date();
        if (!this.userPlanningsToSave.includes(userPlanning)) {
            this.userPlanningsToSave.push(userPlanning);
        }
        this.normalizeUserPlanningsToSave();

        const key =
            this.findProjectDayUserMapKey(day, userId, projectId, rowKey, projectDayUser) ??
            this.makeKey(day, userId, rowKey ?? projectId);

        if (this.projectDayUserMap.has(key)) {
            this.projectDayUserMap.delete(key);
            this.syncDayUserMap(day, userId);
        }

        if (planning?.user_planning) {
            planning.user_planning.splice(planning.user_planning.indexOf(userPlanning), 1);
        }
    }

    /**
     * Writes one material assignment as the driver's active planning for this row and keeps every related map in sync.
     */
    private assignDriverToMaterialPlanning(
        day: { day: Date; project?: Project },
        user: User,
        planningHas: PlanningHasEntity,
        rowKey?: string,
        performerId?: number
    ): void {
        const previousPerformerId = planningHas.performer_id;
        planningHas.driver_user_id = user.id;
        planningHas.performer_id = performerId ?? undefined;
        this.syncPlanningHasMaterialMapEntry(planningHas, day.project?.afas_project_id, rowKey, previousPerformerId);

        if (!this.planningHasToSave.includes(planningHas)) {
            this.planningHasToSave.push(planningHas);
        }

        this.dayUserMap.set(this.makeKey(day.day, user.id), day.project?.afas_project_id);
        this.projectDayUserMap.set(this.makeKey(day.day, user.id, rowKey ?? day.project?.afas_project_id), {
            type: 'project',
            planningHas: [planningHas],
            userPlanning: [],
        });
    }

    addUserPlanningTeam(
        event,
        day: {
            day: Date;
            project?: Project;
        },
        team: {
            entityType: EntityType;
            entity: Entity;
            users: User[];
            showUsers: boolean;
        },
        user: User,
        rowKey?: string
    ) {
        event.preventDefault();
        const project = day.project;
        if (this.readonly || !project) {
            return;
        }
        const resolvedDay = { day: day.day, project };

        const projectsIds = [
            ...new Set([
                ...this.projects.map(p => p.afas_project_id),
                ...this.planningList.map(p => p.afas_project_id ?? p.worknumber),
            ]),
        ];
        const userProjectDay = this.projectDayUserMap.get(
            this.makeKey(day.day, user.id, rowKey ?? day.project?.afas_project_id)
        );

        if (userProjectDay?.type === 'planning') {
            this.confirmDialogService.confirm({
                title: 'Chauffeurs kunnen alleen via de transportplanning geplant worden',
                text: `Deze persoon is op deze dag al ingepland als chauffeur en kan daarom
                    alleen via de transport- of weekplanning ingepland worden.`,
                confirm: 'Sluiten',
            });
            return;
        }

        if (userProjectDay && (userProjectDay.userPlanning?.length || userProjectDay.planningHas?.length)) {
            if (userProjectDay.userPlanning?.length === 1) {
                const planning = this.projectDayTeamMap.get(
                    this.makeKey(day.day, team.entity.id, day.project?.afas_project_id)
                );
                this.removeSingleUserPlanningFromProjectDay(
                    day.day,
                    user.id,
                    day.project?.afas_project_id,
                    rowKey,
                    userProjectDay,
                    planning
                );
            } else {
                this.confirmDialogService.confirm({
                    title: 'Meer keren ingepland',
                    text: `De persoon is op deze dag en dit project meerdere keren ingepland en kan daarom alleen via de transport of weekplanning aangepast worden.`,
                    confirm: 'Sluiten',
                });
                return;
            }
        } else {
            let planning = this.projectDayTeamMap.get(
                this.makeKey(day.day, team.entity.id, day.project?.afas_project_id)
            );
            if (!planning) {
                if (
                    projectsIds
                        .map(
                            projectId =>
                                !!this.projectDayTeamMap.get(this.makeKey(day.day, team.entity.id, projectId))
                                    ?.mainPlanning?.entity?.use_once
                        )
                        .find(p => p)
                ) {
                    this.showAlreadyPlannedOnDayDialog('Deze ploeg');
                    return;
                }
                planning = this.createPlanning(team.entity, team.entityType, resolvedDay);
            }
            const begindate = new Date(Utils.mainPlanning(planning).begindate);
            const enddate = new Date(Utils.mainPlanning(planning).enddate);
            const performerId = rowKey
                ? (this.projectDaysList.find(pd => pd.rowKey === rowKey)?.form.controls.performer_id.value ?? undefined)
                : undefined;

            if (this.userHasPlanningOnDay(day.day, user.id, day.project?.afas_project_id, rowKey, begindate, enddate)) {
                this.showAlreadyPlannedOnDayDialog('De persoon');
                return;
            }

            this.addUserPlanningToPlanningTeam(planning, user, begindate, enddate, resolvedDay, rowKey, performerId);
        }
        this.refreshPlanningView();
        this.markEmployeePlanningUserRowsForCheck([user.id]);
    }

    addUserPlanningProject(
        event,
        day: {
            day: Date;
            project?: Project;
        },
        userProjectDay: ProjectDayUserState | undefined,
        user: User,
        doNotAddEntity = false,
        performerId?: number,
        rowKey?: string
    ) {
        event.preventDefault();
        if (this.readonly) {
            return;
        }

        if (!day.project?.afas_project_id) {
            this.confirmDialogService.confirm({
                title: 'Onbekend project',
                text: `Voor deze planning is nog geen juist Afas project gekozen.`,
                confirm: 'Sluiten',
            });
            return;
        }

        const removableUserProjectDay =
            userProjectDay ??
            this.getProjectDayEntry(this.projectDayUserMap, day.day, user.id, day.project?.afas_project_id, rowKey);

        if (removableUserProjectDay?.type === 'planning') {
            if (removableUserProjectDay.userPlanning?.length + removableUserProjectDay.planningHas?.length === 1) {
                const project = this.planningList.find(
                    p =>
                        p.id ===
                        (removableUserProjectDay?.userPlanning[0]?.planning_id ??
                            removableUserProjectDay?.planningHas[0]?.planning_id)
                );
                this.planningDetailDialogService.openPlanning(project);
            } else {
                this.confirmDialogService.confirm({
                    title: 'Meerdere keren ingepland',
                    text: `De persoon is op deze dag en dit project meerdere keren ingepland en kan daarom
                    alleen via de transport of weekplanning aangepast worden.`,
                    confirm: 'Sluiten',
                });
            }
        } else {
            if (removableUserProjectDay) {
                if (removableUserProjectDay.userPlanning?.length + removableUserProjectDay.planningHas?.length > 1) {
                    this.confirmDialogService.confirm({
                        title: 'Meer keren ingepland',
                        text: `De persoon is op deze dag en dit project meerdere keren ingepland en kan daarom alleen via de transport of weekplanning aangepast worden.`,
                        confirm: 'Sluiten',
                    });
                    return;
                }

                if (removableUserProjectDay.userPlanning?.length === 1) {
                    this.removeSingleUserPlanningFromProjectDay(
                        day.day,
                        user.id,
                        day.project?.afas_project_id,
                        rowKey,
                        removableUserProjectDay
                    );
                }
                if (removableUserProjectDay.planningHas?.length === 1) {
                    if (removableUserProjectDay.planningHas[0].id) {
                        removableUserProjectDay.planningHas[0].driver_user_id = null;
                        if (!this.planningHasToSave.includes(removableUserProjectDay.planningHas[0])) {
                            this.planningHasToSave.push(removableUserProjectDay.planningHas[0]);
                        }
                    } else {
                        const removablePlanningHas = removableUserProjectDay.planningHas[0];
                        const entityKey = this.getProjectDayMaterialMapKeys(day.day, removablePlanningHas.entity_id, {
                            projectId: this.resolveProjectId(removablePlanningHas) ?? day.project?.afas_project_id,
                            rowKey,
                            nonUseOnceName: this.entitiesMap.get(removablePlanningHas.entity_id)?.use_once
                                ? null
                                : removablePlanningHas.name,
                            performerId: removablePlanningHas.performer_id,
                        })[0];
                        if (entityKey) {
                            this.projectDayMaterialMap.delete(entityKey);
                        }
                        if (this.planningHasToSave.includes(removableUserProjectDay.planningHas[0])) {
                            this.planningHasToSave.splice(
                                this.planningHasToSave.indexOf(removableUserProjectDay.planningHas[0]),
                                1
                            );
                        }
                    }

                    const key =
                        this.findProjectDayUserMapKey(
                            day.day,
                            user.id!,
                            day.project?.afas_project_id,
                            rowKey,
                            removableUserProjectDay
                        ) ?? this.makeKey(day.day, user.id, rowKey ?? day.project?.afas_project_id);
                    if (this.projectDayUserMap.has(key)) {
                        this.projectDayUserMap.delete(key);
                        this.syncDayUserMap(day.day, user.id);
                    }
                }

                this.refreshPlanningView();
                this.markEmployeePlanningUserRowsForCheck([user.id]);
            } else {
                const add = () => {
                    const driverFrom = this.entities.find(e => e.driver_user_id === user.id);

                    // Driver entities and plain user plannings now share the same row-aware lookup,
                    // so assigning/removing a driver keeps working even with multiple rows per project.
                    let materialPlanningDriver =
                        driverFrom &&
                        this.getProjectDayMaterialEntry(
                            day.day,
                            driverFrom.id,
                            day.project?.afas_project_id,
                            rowKey,
                            null,
                            performerId
                        );
                    const fromDate = new Date(day.day);
                    const toDate = new Date(day.day);
                    const startTime =
                        60 * (this.fcNightPlanning.value ? Settings.DEFAULT_START_NIGHT : Settings.DEFAULT_START);
                    fromDate.setMinutes(startTime);
                    toDate.setMinutes(startTime);
                    toDate.setMinutes(toDate.getMinutes() + 60 * Settings.DEFAULT_DURATION_OTHER);

                    if (driverFrom && materialPlanningDriver?.length && !doNotAddEntity) {
                        const candidateStart = new Date(materialPlanningDriver[0].begindate);
                        const candidateEnd = new Date(materialPlanningDriver[0].enddate);
                        if (
                            this.userHasPlanningOnDay(
                                day.day,
                                user.id,
                                day.project?.afas_project_id,
                                rowKey,
                                candidateStart,
                                candidateEnd
                            )
                        ) {
                            this.showAlreadyPlannedOnDayDialog('De persoon');
                            return;
                        }

                        this.assignDriverToMaterialPlanning(day, user, materialPlanningDriver[0], rowKey, performerId);
                    }
                    if (driverFrom && !materialPlanningDriver?.length && !doNotAddEntity) {
                        if (this.addEntityPlanning(event, day, driverFrom, null, true, rowKey, performerId)) {
                            materialPlanningDriver = this.getProjectDayMaterialEntry(
                                day.day,
                                driverFrom.id,
                                day.project?.afas_project_id,
                                rowKey,
                                null,
                                performerId
                            );

                            if (materialPlanningDriver?.length) {
                                const candidateStart = new Date(materialPlanningDriver[0].begindate);
                                const candidateEnd = new Date(materialPlanningDriver[0].enddate);
                                if (
                                    this.userHasPlanningOnDay(
                                        day.day,
                                        user.id,
                                        day.project?.afas_project_id,
                                        rowKey,
                                        candidateStart,
                                        candidateEnd
                                    )
                                ) {
                                    this.showAlreadyPlannedOnDayDialog('De persoon');
                                    return;
                                }

                                this.assignDriverToMaterialPlanning(
                                    day,
                                    user,
                                    materialPlanningDriver[0],
                                    rowKey,
                                    performerId
                                );
                            } else {
                                doNotAddEntity = true;
                            }
                        } else {
                            doNotAddEntity = true;
                        }
                    }
                    if (!driverFrom || doNotAddEntity) {
                        // A project row can inherit times from the linked asphalt planning,
                        // but only when the selected row represents the configured performer.
                        const selectedProjectDay =
                            (rowKey
                                ? this.projectDaysList?.find(projectDay => projectDay.rowKey === rowKey)
                                : undefined) ??
                            this.getProjectDayForPerformer(day.project?.afas_project_id, performerId, true);
                        const linkedPlanning = this.getLinkedPlanningForProjectDay(day.day, selectedProjectDay);
                        if (linkedPlanning) {
                            // Matching performer rows inherit the asphalt-planning times,
                            // but only when that planning is also a day-planning or night-planning in this view.
                            fromDate.setTime(new Date(Utils.mainPlanning(linkedPlanning).begindate).getTime());
                            toDate.setTime(new Date(Utils.mainPlanning(linkedPlanning).enddate).getTime());
                        }

                        if (
                            this.userHasPlanningOnDay(
                                day.day,
                                user.id,
                                day.project?.afas_project_id,
                                rowKey,
                                fromDate,
                                toDate
                            )
                        ) {
                            this.showAlreadyPlannedOnDayDialog('De persoon');
                            return;
                        }

                        const userPlanning = new UserPlanning();
                        userPlanning.afas_project_id = day.project?.afas_project_id;
                        userPlanning.user_id = user.id!;
                        userPlanning.begindate = fromDate;
                        userPlanning.enddate = toDate;
                        const shouldLinkToPlanning =
                            !!linkedPlanning?.id &&
                            selectedProjectDay ===
                                this.projectDaysList.find(
                                    projectDay => projectDay.projectId === selectedProjectDay?.projectId
                                );
                        if (shouldLinkToPlanning) {
                            userPlanning.planning_id = linkedPlanning.id;
                            userPlanning.planning = linkedPlanning;
                        }
                        if (performerId) {
                            userPlanning.performer_id = performerId;
                        }

                        this.storeUserPlanningOnProjectDay(
                            userPlanning,
                            day,
                            user.id,
                            shouldLinkToPlanning ? 'userPlanning' : 'project',
                            rowKey,
                            shouldLinkToPlanning ? linkedPlanning : undefined
                        );
                    }

                    this.refreshPlanningView();
                    // A confirmation can finish after the row's click check, so explicitly refresh only this user.
                    this.markEmployeePlanningUserRowsForCheck([user.id]);
                };

                const leave = this.leaveDayUserMap.get(this.makeKey(day.day, user.id));
                if (leave) {
                    this.confirmDialogService
                        .confirm({
                            title: 'Let op: medewerker heeft verlof!',
                            text: `Deze medewerker heeft verlof op deze dag, wilt u deze persoon toch inplannen?`,
                            confirm: 'Toch inplannen',
                            cancel: 'Annuleren',
                        })
                        .then(() => add());
                } else {
                    add();
                }
            }
        }
    }

    addressSelectedForProject(address: FormattedAddress | null, projectDays: ProjectDay): void {
        if (!address?.addressLine) {
            return;
        }

        const numberIndex = address.addressLine.search(/\d/);
        const street = address.addressLine.slice(0, numberIndex < 5 ? address.addressLine.length : numberIndex).trim();
        const houseNumber = address.addressLine.replace(street, '');
        projectDays.form.controls.location_postcode.setValue(address.postalCode);
        projectDays.form.controls.location_housenumber.setValue(houseNumber);
        projectDays.form.controls.location_straat.setValue(street);
        projectDays.form.controls.location_place.setValue(address.locality);
    }

    undo() {
        setTimeout(() => {
            this.rollingBack = true;
        });
        setTimeout(() => {
            // Rollback any queued performer changes to their previous values.
            this.projectDayPerformerChangesToSave.forEach(change => {
                const projectDays = this.projectDaysList?.find(projectDay => projectDay.rowKey === change.rowKey);
                if (projectDays) {
                    projectDays.form.controls.performer_id.setValue(
                        (change.previousPerformerId ?? undefined) as number,
                        {
                            emitEvent: false,
                        }
                    );
                }
                this.applyQueuedProjectDayPerformerValue(
                    change.userPlannings,
                    change.previousPerformerId,
                    change.planningHass,
                    change.rowKey
                );
            });
            // Rollback any queued user planning changes to their previous values.
            this.userPlanningsToSave.forEach(userPlanning => {
                if (userPlanning.deleted_at) {
                    delete userPlanning.deleted_at;
                }
            });
            this.projectDayUserMap.clear();
            this.projectDayTeamMap.clear();
            this.userWeekCount.clear();
            this.dateHasUserMap.clear();
            this.projectDayMaterialMap.clear();
            this.leaveDayUserMap.clear();
            this.afasAbsenceUserMap.clear();
            this.userPlanningsToSave = [];
            this.planningHasToSave = [];
            this.planningsToSave = [];
            this.leavesToSave = [];
            this.projectDayPerformerChangesToSave = [];
            this.leavesToMap(this.leaveList, this.allUsers);
            this.parseData(
                this.planningList,
                this.projects,
                this.planningHass,
                this.userPlannings,
                this.usersMap,
                this.entitiesMap
            );
            this.rollingBack = false;
            this.projectDayTeamMap.forEach((planning, key) => {
                const mPlanning = Utils.mainPlanning(planning);
                planning.user_planning
                    .filter(up => !up.id)
                    .forEach(up => {
                        this.projectDayUserMap.delete(
                            this.makeKey(new Date(mPlanning.begindate), up.user_id, planning.afas_project_id)
                        );
                        this.syncDayUserMap(new Date(mPlanning.begindate), up.user_id);
                    });
                planning.user_planning = planning.user_planning.filter(up => !!up.id);
            });
        }, 50);
    }

    save() {
        if (this.readonly) {
            return;
        }

        // Apply queued performer changes to the user plannings and collect them for saving.
        this.projectDayPerformerChangesToSave.forEach(change => {
            this.applyQueuedProjectDayPerformerValue(
                change.userPlannings,
                change.nextPerformerId,
                change.planningHass,
                change.rowKey
            );

            change.userPlannings.forEach(queuedUserPlanning => {
                const userPlanning = this.findQueuedProjectDayPerformerUserPlanning(queuedUserPlanning);
                if (
                    userPlanning &&
                    !this.userPlanningsToSave.some(existingUserPlanning =>
                        this.sameEntityOrSavedId(existingUserPlanning, userPlanning)
                    )
                ) {
                    this.userPlanningsToSave.push(userPlanning);
                }
            });

            change.planningHass.forEach(queuedPlanningHas => {
                const planningHas = this.findQueuedProjectDayPerformerPlanningHas(queuedPlanningHas);
                if (
                    planningHas &&
                    !this.planningHasToSave.some(existingPlanningHas =>
                        this.sameEntityOrSavedId(existingPlanningHas, planningHas)
                    )
                ) {
                    this.planningHasToSave.push(planningHas);
                }
            });
        });
        this.normalizeUserPlanningsToSave();

        this.saving = true;
        const saveCalls = [];
        // Save deletions before creates/updates from the same batch.
        // The backend duplicate check looks at the database state while it processes the array,
        // so a delete+re-add for the same slot can be rejected as "duplicate" if the create is handled first.
        let userPlanningsToSave = [...this.userPlanningsToSave].sort((left, right) => {
            return Number(!!right.deleted_at) - Number(!!left.deleted_at) || Number(!!right.id) - Number(!!left.id);
        });
        // Same idea for driver/material assignments: remove the old record first, then write the replacement.
        const planningHasToSave = [...this.planningHasToSave].sort((left, right) => {
            return Number(!!right.deleted_at) - Number(!!left.deleted_at) || Number(!!right.id) - Number(!!left.id);
        });

        userPlanningsToSave = userPlanningsToSave.filter(
            userPlanning =>
                userPlanning.id || userPlanning.deleted_at || !this.findActiveExistingUserPlanning(userPlanning)
        );

        if (this.leavesToSave?.length) {
            this.leavesToSave.forEach(leave => {
                saveCalls.push(
                    from(this.leaveService.saveLeave({ ...leave, users: leave.users?.map(u => u.id) })).pipe(
                        tap(p => {
                            leave.id = p.data.id;
                            leave.updated_at = p.data.updated_at;
                        })
                    )
                );
            });
            this.leavesToSave = [];
        }

        if (this.planningsToSave?.length) {
            this.planningsToSave.forEach((planning, index) => {
                const pl = { ...planning };
                delete pl.user_planning;
                saveCalls.push(
                    from(this.planningService.saveOld(pl)).pipe(
                        tap(p => {
                            planning.id = p.id;
                            planning.user_planning
                                .filter(up => !up.id)
                                .forEach(up => {
                                    up.planning_id = planning.id;
                                    up.planning = null;
                                });
                        })
                    )
                );
            });
        }
        userPlanningsToSave
            .filter(up => !!up.planning_id)
            .forEach(up => {
                up.planning = null;
            });
        saveCalls.push(
            this.userPlanningService.saveUserPlanningProject(userPlanningsToSave, planningHasToSave).pipe(
                tap(response => {
                    const [savedUserPlannings, savedPlanningHas] = response.data ?? [[], []];
                    // Keep working with the original in-memory objects so the rest of this method
                    // and the next websocket refresh see the newly assigned ids instead of a "new" item twice.
                    userPlanningsToSave.forEach((userPlanning, index) => {
                        const savedUserPlanning = savedUserPlannings[index];
                        if (!userPlanning.deleted_at && savedUserPlanning?.id) {
                            delete userPlanning.deleted_at;
                            Object.assign(userPlanning, savedUserPlanning);
                        }
                    });
                    planningHasToSave.forEach((planningHas, index) => {
                        const savedPlanningHasItem = savedPlanningHas[index];
                        if (!planningHas.deleted_at && savedPlanningHasItem?.id) {
                            delete planningHas.deleted_at;
                            Object.assign(planningHas, savedPlanningHasItem);
                        }
                    });

                    userPlanningsToSave.forEach(up => {
                        if (up.deleted_at) {
                            // Removal: clean up from maps
                            const planning = this.planningList.find(planning => planning.id === up.planning_id);
                            const afasProjectId = planning?.afas_project_id ?? up?.afas_project_id;
                            const projectDay =
                                up.performer_id != null
                                    ? this.getProjectDayForPerformer(afasProjectId, up.performer_id, !up.planning_id)
                                    : this.projectDaysList.find(pd => pd.projectId === afasProjectId);
                            const mapKey = projectDay?.rowKey ?? afasProjectId;

                            const key = this.makeKey(new Date(up.begindate), up.user_id, mapKey);

                            const userPlannings = this.projectDayUserMap.get(key);
                            if (userPlannings) {
                                // After merging queued items by id, the instance coming back from save handling
                                // is not guaranteed to be the same object reference as the one in the map.
                                const userPlanningIndex = userPlannings.userPlanning?.findIndex(
                                    existingUserPlanning =>
                                        existingUserPlanning === up || (!!up.id && existingUserPlanning.id === up.id)
                                );
                                if (userPlanningIndex > -1) {
                                    userPlannings.userPlanning.splice(userPlanningIndex, 1);
                                }
                            }
                            if (userPlannings) {
                                this.projectDayUserMap.set(key, userPlannings);
                            }
                            if (!userPlannings?.userPlanning?.length && !userPlannings?.planningHas?.length) {
                                this.projectDayUserMap.delete(key);
                                this.syncDayUserMap(new Date(up.begindate), up.user_id);
                            }
                            // Same lookup rule here: remove the stored item by object reference or by persisted id.
                            const idx = this.userPlannings.findIndex(
                                existingUserPlanning =>
                                    existingUserPlanning === up || (!!up.id && existingUserPlanning.id === up.id)
                            );
                            if (idx !== -1) {
                                this.userPlannings.splice(idx, 1);
                            }
                        } else if (
                            !this.userPlannings.some(
                                existingUserPlanning =>
                                    existingUserPlanning === up || (!!up.id && existingUserPlanning.id === up.id)
                            )
                        ) {
                            this.userPlannings.push(up);
                        }
                    });
                    this.userPlanningsToSave = [];
                    this.projectDayPerformerChangesToSave.forEach(change => {
                        const projectDays = this.projectDaysList?.find(
                            projectDay => projectDay.rowKey === change.rowKey
                        );
                        if (projectDays) {
                            projectDays.syncedPerformerId = projectDays.form.controls.performer_id.value ?? undefined;
                        }
                    });
                    this.projectDayPerformerChangesToSave = [];
                    planningHasToSave.forEach(ph => {
                        const projectId = ph.afas_project_id ?? ph.planning?.afas_project_id ?? ph.planning?.worknumber;
                        this.removePlanningHasMaterialMapEntry(ph, projectId, undefined, ph.performer_id);

                        const userMapKey =
                            this.getProjectDayForPerformer(projectId, ph.performer_id, !ph.planning_id)?.rowKey ??
                            projectId;
                        const userKey =
                            ph.driver_user_id && userMapKey
                                ? this.makeKey(new Date(ph.begindate), ph.driver_user_id, userMapKey)
                                : undefined;

                        const planningHasListUser = userKey ? this.projectDayUserMap.get(userKey) : undefined;
                        if (planningHasListUser?.planningHas) {
                            planningHasListUser.planningHas.splice(planningHasListUser.planningHas.indexOf(ph), 1);
                            this.projectDayUserMap.set(userKey!, planningHasListUser);
                        }
                        if (!planningHasListUser?.userPlanning?.length && !planningHasListUser?.planningHas?.length) {
                            if (userKey) {
                                this.projectDayUserMap.delete(userKey);
                            }
                        }
                        this.syncDayUserMap(new Date(ph.begindate), ph.driver_user_id);
                    });
                    this.planningHasToSave = [];
                    this.mergeDuplicatePerformerProjectRows();
                    this.refreshPlanningView();
                })
            )
        );

        concat(...saveCalls).subscribe(
            result => {
                this.planningsToSave = [];
                this.saving = false;
            },
            error => {
                console.error(error);
                this.saving = false;
            }
        );

        this.projectDaysList.forEach(projectDays => {
            // Only save project info from the base row — not from variant rows that share the same project object
            const isBaseRow = this.projectDaysList.find(pd => pd.projectId === projectDays.projectId) === projectDays;
            if (projectDays.form.dirty && projectDays.project && isBaseRow) {
                projectDays.project.location = projectDays.form.value.location;
                projectDays.project.location_postcode = projectDays.form.value.location_postcode;
                projectDays.project.location_housenumber = projectDays.form.value.location_housenumber;
                projectDays.project.location_straat = projectDays.form.value.location_straat;
                projectDays.project.location_place = projectDays.form.value.location_place;
                projectDays.project.projectmanager_id = projectDays.form.value.projectmanager_id;
                this.projectService.saveProject(projectDays.project).subscribe(
                    p => {
                        projectDays.project.updated_at = p.data.updated_at;
                        projectDays.form.markAsPristine();
                        this.saving = false;
                    },
                    () => {
                        this.saving = false;
                    }
                );
            } else if (projectDays.form.dirty) {
                // Performer changes live on user_planning, so rows without project field edits do not write the project.
                projectDays.form.markAsPristine();
            }
        });
    }

    /** Returns true when two values refer to the same saved or in-memory item. */
    private sameEntityOrSavedId<T extends { id?: number }>(left: T, right: T): boolean {
        return left === right || (!!left.id && left.id === right.id);
    }

    /** Appends only non-matching items from `right` into `left`. */
    private mergeUniqueItems<T>(left: T[] = [], right: T[] = [], matches: (left: T, right: T) => boolean): T[] {
        const result = [...left];

        right.forEach(item => {
            if (!result.some(existingItem => matches(existingItem, item))) {
                result.push(item);
            }
        });

        return result;
    }

    /** Converts one user_planning into the lightweight performer-change payload. */
    private toQueuedProjectDayPerformerUserPlanning(userPlanning: UserPlanning): QueuedProjectDayPerformerUserPlanning {
        return {
            id: userPlanning.id,
            user_id: userPlanning.user_id,
            begindate: userPlanning.begindate,
            enddate: userPlanning.enddate,
            planning_id: userPlanning.planning_id,
            afas_project_id: this.resolveProjectId(userPlanning),
        };
    }

    /** Converts one planning_has row into the lightweight performer-change payload. */
    private toQueuedProjectDayPerformerPlanningHas(
        planningHas: PlanningHasEntity
    ): QueuedProjectDayPerformerPlanningHas {
        return {
            id: planningHas.id,
            entity_id: planningHas.entity_id,
            begindate: planningHas.begindate,
            enddate: planningHas.enddate,
            planning_id: planningHas.planning_id,
            afas_project_id: this.resolveProjectId(planningHas),
            name: planningHas.name,
        };
    }

    /** Matches queued user_planning descriptors by id or by their identifying time-slot fields. */
    private isSameQueuedProjectDayPerformerUserPlanning(
        left: QueuedProjectDayPerformerUserPlanning,
        right: QueuedProjectDayPerformerUserPlanning
    ): boolean {
        return (
            this.sameEntityOrSavedId(left, right) ||
            (!left.id &&
                !right.id &&
                left.user_id === right.user_id &&
                Utils.getTimeOrNull(left.begindate) === Utils.getTimeOrNull(right.begindate) &&
                Utils.getTimeOrNull(left.enddate) === Utils.getTimeOrNull(right.enddate) &&
                (left.planning_id ?? null) === (right.planning_id ?? null) &&
                (left.afas_project_id ?? null) === (right.afas_project_id ?? null))
        );
    }

    /** Matches queued planning_has descriptors by id or by their identifying assignment fields. */
    private isSameQueuedProjectDayPerformerPlanningHas(
        left: QueuedProjectDayPerformerPlanningHas,
        right: QueuedProjectDayPerformerPlanningHas
    ): boolean {
        return (
            this.sameEntityOrSavedId(left, right) ||
            (!left.id &&
                !right.id &&
                left.entity_id === right.entity_id &&
                Utils.getTimeOrNull(left.begindate) === Utils.getTimeOrNull(right.begindate) &&
                Utils.getTimeOrNull(left.enddate) === Utils.getTimeOrNull(right.enddate) &&
                (left.planning_id ?? null) === (right.planning_id ?? null) &&
                (left.afas_project_id ?? null) === (right.afas_project_id ?? null) &&
                (left.name ?? null) === (right.name ?? null))
        );
    }

    /** Collects the user_planning rows that currently belong to one visible project row. */
    private getProjectDayRowUserPlannings(rowKey: string): UserPlanning[] {
        return Array.from(this.projectDayUserMap.entries())
            .filter(([key]) => key.endsWith(`-${rowKey}`))
            .flatMap(([, projectDayUser]) => projectDayUser.userPlanning ?? []);
    }

    /** Collects the planning_has rows that currently belong to one visible project row. */
    private getProjectDayRowPlanningHass(rowKey: string): PlanningHasEntity[] {
        const rowPlanningHass = Array.from(this.projectDayUserMap.entries())
            .filter(([key]) => key.endsWith(`-${rowKey}`))
            .flatMap(([, projectDayUser]) => projectDayUser.planningHas ?? []);
        const rowMaterialPlanningHass = Array.from(this.projectDayMaterialMap.entries())
            .filter(([key]) => key.includes(`-${rowKey}`))
            .flatMap(([, projectDayMaterial]) => projectDayMaterial);

        return this.mergeUniqueItems(rowPlanningHass, rowMaterialPlanningHass, (left, right) =>
            this.sameEntityOrSavedId(left, right)
        );
    }

    /** Collects every user_planning instance a performer change may need to update. */
    private getAvailableUserPlanningsForPerformerChange(
        userPlannings: UserPlanning[] = this.userPlannings ?? []
    ): UserPlanning[] {
        return this.mergeUniqueItems(
            [...userPlannings, ...this.userPlanningsToSave],
            Array.from(this.projectDayUserMap.values()).flatMap(projectDayUser => projectDayUser.userPlanning ?? []),
            (left, right) => this.sameEntityOrSavedId(left, right)
        );
    }

    /** Collects every planning_has instance a performer change may need to update. */
    private getAvailablePlanningHassForPerformerChange(
        planningHass: PlanningHasEntity[] = this.planningHass ?? []
    ): PlanningHasEntity[] {
        return this.mergeUniqueItems(
            [...planningHass, ...this.planningHasToSave],
            Array.from(this.projectDayUserMap.values())
                .flatMap(projectDayUser => projectDayUser.planningHas ?? [])
                .concat(
                    Array.from(this.projectDayMaterialMap.values()).flatMap(projectDayMaterial => projectDayMaterial)
                ),
            (left, right) => this.sameEntityOrSavedId(left, right)
        );
    }

    /** Finds the concrete user_planning instance for one queued performer-change descriptor. */
    private findQueuedProjectDayPerformerUserPlanning(
        queuedUserPlanning: QueuedProjectDayPerformerUserPlanning,
        userPlannings: UserPlanning[] = this.userPlannings ?? []
    ): UserPlanning | undefined {
        return this.getAvailableUserPlanningsForPerformerChange(userPlannings).find(userPlanning =>
            !!queuedUserPlanning.id
                ? userPlanning.id === queuedUserPlanning.id
                : !userPlanning.deleted_at &&
                  this.isSameQueuedProjectDayPerformerUserPlanning(
                      this.toQueuedProjectDayPerformerUserPlanning(userPlanning),
                      queuedUserPlanning
                  )
        );
    }

    /** Finds the concrete planning_has instance for one queued performer-change descriptor. */
    private findQueuedProjectDayPerformerPlanningHas(
        queuedPlanningHas: QueuedProjectDayPerformerPlanningHas,
        planningHass: PlanningHasEntity[] = this.planningHass ?? []
    ): PlanningHasEntity | undefined {
        return this.getAvailablePlanningHassForPerformerChange(planningHass).find(planningHas =>
            !!queuedPlanningHas.id
                ? planningHas.id === queuedPlanningHas.id
                : !planningHas.deleted_at &&
                  this.isSameQueuedProjectDayPerformerPlanningHas(
                      this.toQueuedProjectDayPerformerPlanningHas(planningHas),
                      queuedPlanningHas
                  )
        );
    }

    /** Returns true when the given planning_has should currently live under this project row. */
    private planningHasBelongsToProjectDayRow(
        planningHas: PlanningHasEntity,
        projectDays: ProjectDay,
        availableUserPlannings: UserPlanning[]
    ): boolean {
        return (
            !planningHas.deleted_at &&
            this.resolveProjectId(planningHas) === projectDays.projectId &&
            (this.getProjectDayForPerformer(
                projectDays.projectId,
                this.resolvePlanningHasPerformerId(planningHas, projectDays.projectId, availableUserPlannings),
                !planningHas.planning_id
            )?.rowKey ?? projectDays.projectId) === projectDays.rowKey
        );
    }

    /** Builds the queued user/material payload for one row-level performer change. */
    private buildProjectDayPerformerChangePayload(
        projectDays: ProjectDay,
        previousPerformerId: number | null,
        existingChange?: ProjectDayPerformerChange
    ): Pick<ProjectDayPerformerChange, 'userPlannings' | 'planningHass'> {
        const availableUserPlannings = this.getAvailableUserPlanningsForPerformerChange();
        const rowUserPlannings = this.getProjectDayRowUserPlannings(projectDays.rowKey);
        const rowPlanningHass = this.getProjectDayRowPlanningHass(projectDays.rowKey);
        const fallbackUserPlannings = this.mergeUniqueItems(
            [],
            availableUserPlannings.filter(
                userPlanning =>
                    !userPlanning.deleted_at &&
                    this.resolveProjectId(userPlanning) === projectDays.projectId &&
                    (userPlanning.performer_id ?? null) === previousPerformerId
            ),
            (left, right) =>
                this.isSameQueuedProjectDayPerformerUserPlanning(
                    this.toQueuedProjectDayPerformerUserPlanning(left),
                    this.toQueuedProjectDayPerformerUserPlanning(right)
                )
        );
        const fallbackPlanningHass = this.mergeUniqueItems(
            [],
            this.getAvailablePlanningHassForPerformerChange().filter(planningHas =>
                this.planningHasBelongsToProjectDayRow(planningHas, projectDays, availableUserPlannings)
            ),
            (left, right) =>
                this.isSameQueuedProjectDayPerformerPlanningHas(
                    this.toQueuedProjectDayPerformerPlanningHas(left),
                    this.toQueuedProjectDayPerformerPlanningHas(right)
                )
        );
        const queuedUserPlannings = (rowUserPlannings.length ? rowUserPlannings : fallbackUserPlannings).map(
            userPlanning => this.toQueuedProjectDayPerformerUserPlanning(userPlanning)
        );
        const queuedPlanningHass = (rowPlanningHass.length ? rowPlanningHass : fallbackPlanningHass).map(planningHas =>
            this.toQueuedProjectDayPerformerPlanningHas(planningHas)
        );

        return {
            userPlannings: queuedUserPlannings.length ? queuedUserPlannings : (existingChange?.userPlannings ?? []),
            planningHass: queuedPlanningHass.length ? queuedPlanningHass : (existingChange?.planningHass ?? []),
        };
    }

    /** Applies one queued performer change back onto the live user_planning and planning_has objects. */
    private applyQueuedProjectDayPerformerValue(
        queuedUserPlannings: QueuedProjectDayPerformerUserPlanning[],
        performerId: number | null,
        queuedPlanningHass: QueuedProjectDayPerformerPlanningHas[] = [],
        rowKey?: string,
        userPlannings: UserPlanning[] = this.userPlannings ?? [],
        planningList: Planning[] = this.planningList ?? [],
        planningHass: PlanningHasEntity[] = this.planningHass ?? []
    ): void {
        queuedUserPlannings.forEach(queuedUserPlanning => {
            const userPlanning = this.findQueuedProjectDayPerformerUserPlanning(queuedUserPlanning, userPlannings);
            if (userPlanning) {
                userPlanning.performer_id = performerId ?? undefined;
            }

            planningList.forEach(planning => {
                planning.user_planning
                    ?.filter(
                        planningUserPlanning =>
                            planningUserPlanning === userPlanning ||
                            (!!userPlanning?.id && planningUserPlanning.id === userPlanning.id)
                    )
                    .forEach(planningUserPlanning => {
                        planningUserPlanning.performer_id = performerId ?? undefined;
                    });
            });
        });

        queuedPlanningHass.forEach(queuedPlanningHas => {
            const planningHas = this.findQueuedProjectDayPerformerPlanningHas(queuedPlanningHas, planningHass);
            if (!planningHas) {
                return;
            }

            const previousPerformerId = planningHas.performer_id;
            planningHas.performer_id = performerId ?? undefined;
            this.syncPlanningHasMaterialMapEntry(
                planningHas,
                this.resolveProjectId(planningHas, planningList),
                rowKey,
                previousPerformerId
            );
        });
    }

    private clearMaps() {
        this.teardownPlanningPointerOverListener();
        this.leaveDayUserMap.clear();
        this.afasAbsenceUserMap.clear();
        this.entityHiringNames.clear();
        this.projectDaysList = [];
        this.initialProjectDaysFiltered = false;
        this.projectDayUserMap.clear();
        this.projectDayTeamMap.clear();
        this.userDayConflictMap.clear();
        this.userWeekCount.clear();
        this.dateHasUserMap.clear();
        this.entityWeekUsedDays.clear();
    }

    next() {
        this.openChangesBackActionCheck().then(result => {
            if (result) {
                this.dataLoadInProgress = true;
                this.clearMaps();
                let week = this.week + 1;
                let year = this.year;
                if (week > 52) {
                    week = 1;
                    year += 1;
                }
                this.router.navigate([`personeelsplanning`, week, year]);
            }
        });
    }

    prev() {
        this.openChangesBackActionCheck().then(result => {
            if (result) {
                this.dataLoadInProgress = true;
                this.clearMaps();
                let week = this.week - 1;
                let year = this.year;
                if (week < 1) {
                    week = 52;
                    year -= 1;
                }
                this.router.navigate([`personeelsplanning`, week, year]);
            }
        });
    }

    leavesToMap(leaves: Leave[], users: User[]) {
        leaves.forEach(leave => {
            this.employeeService.leaveToMap(this.leaveDayUserMap, leave, users);
        });
    }

    /**
     * Move this to a separate function to avoid creating additional subscriptions to the user and leave streams.
     */
    private syncLeaveDayUserMap(): void {
        this.leaveDayUserMap.clear();

        if (!Array.isArray(this.allUsers) || !Array.isArray(this.leaveList)) {
            return;
        }

        this.leavesToMap(this.leaveList, this.allUsers);
    }

    getData() {
        this.dataLoadInProgress = true;
        const requestFromDate = new Date(this.fromDate);
        const requestToDate = new Date(this.toDate);

        // --- Observable definitions ---
        const planning$ = this.planningService.getFilteredList(requestFromDate, requestToDate);
        const project$ = this.projectService.getFilteredList(requestFromDate, requestToDate);
        const planningHas$ = this.planningHasService.getFilteredList(requestFromDate, requestToDate);
        const userPlanning$ = this.userPlanningService.getFilteredList(requestFromDate, requestToDate);

        // AFAS absence data is optional: skipped when AFAS is disabled or in read-only mode.
        const leaves$ =
            environment.disableAfas || this.readonly
                ? of(null)
                : this.afasService.getLeaves(requestFromDate, requestToDate);
        const illness$ =
            environment.disableAfas || this.readonly
                ? of(null)
                : this.afasService.getIllness(requestFromDate, requestToDate);

        const planningUsers$ = this.userService
            .getList(false, true)
            .pipe(
                map(users =>
                    users.sort((a, b) =>
                        (a.lastname ?? '').localeCompare(b.lastname ?? '', 'nl', { sensitivity: 'base', numeric: true })
                    )
                )
            );
        const rolesMap$ = this.authorisationService.getRolesMap();
        const entities$ = this.entitiesService.getList(this.fromDate, this.toDate);
        const entityTypes$ = this.entitiesService.getTypesMap();

        const leave$ = this.leaveService.getFilteredList(this.fromDate, this.toDate);

        this.blockedMap = null;
        this.periodSubscriptions?.unsubscribe();
        this.periodSubscriptions = new Subscription();

        // --- Subscription 1: autocomplete for hiring names ---
        this.periodSubscriptions.add(
            this.fcAddHiring.valueChanges.pipe(debounceTime(200)).subscribe(value => {
                this.periodSubscriptions.add(
                    this.planningService.getHiringNames(this.addingHiringForEntityId).subscribe(names => {
                        const searchValue = (value ?? '').toLowerCase();
                        let newHirings = names.data.filter(name => name?.toLowerCase().includes(searchValue));
                        if (value?.length) {
                            newHirings.push(value);
                        }
                        const currentNames = this.entityHiringNames.get(this.addingHiringForEntityId) ?? [];
                        newHirings = newHirings
                            .filter(newHiring => currentNames.indexOf(newHiring) === -1 && newHiring?.length > 0)
                            .slice(-5);
                        this.hiringAutocompleteOptions = newHirings;
                    })
                );
            })
        );

        // --- Subscription 2: leave data ---
        // Kept separate from the main combineLatest so that leave updates don't trigger a full planning re-parse.
        this.periodSubscriptions.add(
            leave$.subscribe(leaves => {
                this.leaveData = leaves;
                this.leaveList = [...leaves];
                this.syncLeaveDayUserMap();
                this.leaveDayUserMap = new Map(this.leaveDayUserMap);
            })
        );

        // --- Subscription 3: main planning data ---
        // Waits for all core data sources before processing. On each emission
        // (e.g. websocket push) the full planning grid is rebuilt.
        const handleData = ([
            planningHas,
            userPlanning,
            planningList,
            projects,
            planningUsers,
            rolesMap,
            entities,
            entityTypeMap,
        ]: [
            PlanningHasEntity[],
            UserPlanning[],
            Planning[],
            Project[],
            User[],
            Map<string, Role>,
            Entity[],
            Map<string, EntityType>,
        ]) => {
            this.usersMap = new Map(planningUsers.filter(user => user.id).map(user => [user.id!, user]));
            const entitiesMap = new Map(entities.filter(entity => entity.id).map(entity => [entity.id, entity]));
            this.entitiesMap = entitiesMap;
            this.entityTypeMap = entityTypeMap;
            this.allUsers = planningUsers;
            this.projectManagers = planningUsers.filter(user =>
                user.roles?.some(role => role.key === this.environment.roles.Projectmanager)
            );
            this.executors = planningUsers.filter(user =>
                user.roles?.some(role => role.key === this.environment.roles.Executor)
            );
            this.syncLeaveDayUserMap();

            // Build users, uniqueRoles and teams. In asfaltploegen-mode
            // users and roles are empty — only teams (ploegen) are relevant.
            const plannableUsers = planningUsers.filter(user =>
                AuthorisationService.hasFunctionality(Functionalities.Plannable, user)
            );

            if (this.router.routerState.snapshot.url.substring(0, 14) === '/asfaltploegen') {
                this.asfaltploegenModus = true;
            }

            const users = this.asfaltploegenModus ? [] : plannableUsers;

            // Each unique role gets the list of users that have that role.
            // seenUserIds ensures a user only appears in their primary (first matching) role.
            this.uniqueRoles = this.asfaltploegenModus
                ? []
                : (() => {
                      const uniqueRolesSource = Array.from(rolesMap.values()).filter(
                          (role: Role) =>
                              role.is_unique &&
                              role.key !== this.environment.roles.Customer &&
                              (!role.restricted || AuthorisationService.hasRole(this.environment.roles.Codalt))
                      );
                      const seenUserIds = new Set<number>();
                      return uniqueRolesSource.reduce(
                          (acc, role: Role) => {
                              const roleUsers = users.filter(
                                  user =>
                                      user.roles?.some((userRole: Role) => userRole.key === role.key) &&
                                      !seenUserIds.has(user.id)
                              );
                              if (roleUsers.length > 0) {
                                  roleUsers.forEach(user => seenUserIds.add(user.id ?? 0));
                                  acc.push({ ...role, users: roleUsers });
                              }
                              return acc;
                          },
                          [] as Array<Role & { users: User[] }>
                      );
                  })();

            // Only entities that are a team-type AND have >1 known members are shown.
            this.teams = entities
                .filter(
                    e =>
                        !!e.entitytypes.find(t => t.is_team) &&
                        e.default_employees?.filter(df => this.usersMap.has(df))?.length > 1
                )
                .map(team => {
                    const teamUserIds = new Set(team.default_employees?.map(employeeId => +employeeId) ?? []);
                    return {
                        entityType: team.entitytypes.find(e => e.is_team),
                        entity: team,
                        users: planningUsers.filter(user => user.id && teamUserIds.has(user.id)),
                        showUsers: this.asfaltploegenModus,
                    };
                })
                .sort((a, b) => a.entityType.name.localeCompare(b.entityType.name));

            this.users = users;
            const selectedRoleKeys = this.fcRoleKeys.value ?? [];
            const validRoleKeys = selectedRoleKeys.filter(roleKey =>
                this.uniqueRoles?.some(role => role.key === roleKey)
            );
            if (selectedRoleKeys.length !== validRoleKeys.length) {
                this.fcRoleKeys.setValue(validRoleKeys);
            }
            this.smallMaterial = entities.filter(e => !!e.entitytypes.find(e => e.visible_small) && !!e.use_once);

            this.hireEntities = entities.filter(
                e =>
                    !!e.entitytypes.find(e => e.visible_small) &&
                    !e.use_once &&
                    [EntityId.HiringEmployee, EntityId.HiringMaterials].indexOf(e.id) !== -1
            );
            this.entities = entities;

            // Once blockedMap is initialised, absence data is loaded once
            // and merged into the view via a nested subscription.
            if (!this.blockedMap) {
                this.blockedMap = new Map<
                    string,
                    { icon: string; description: string; startDate: Date; endDate: Date }
                >();
                this.periodSubscriptions.add(
                    combineLatest([leaves$, illness$]).subscribe(([leaves, illness]) => {
                        const leavesAndIlls = [
                            ...(illness?.data.map(ill => {
                                return {
                                    startDate: ill.startDate,
                                    endDate: ill.endDate,
                                    description: ill.absenceTypeDesc,
                                    employeeId: ill.employeeId,
                                    icon: 'fa-face-thermometer',
                                };
                            }) ?? []),
                            ...(leaves?.data.map(leave => {
                                return {
                                    startDate: leave.startDate,
                                    endDate: leave.endDate,
                                    description: leave.leaveDescr,
                                    employeeId: leave.employeeId,
                                    icon: 'fa-user-slash',
                                };
                            }) ?? []),
                        ];

                        const loopDate = new Date(this.fromDate);
                        while (loopDate.getTime() < this.toDate.getTime()) {
                            const startOfDay = Utils.setTime(new Date(loopDate), 0, 0);
                            const endOfDay = Utils.setTime(new Date(loopDate), 23, 59);
                            leavesAndIlls.forEach(leaveOrIll => {
                                const user = this.users.find(u => u.afas_employee_id === leaveOrIll.employeeId);
                                if (
                                    Utils.newDate(leaveOrIll.startDate) <= endOfDay &&
                                    (leaveOrIll.endDate === null || Utils.newDate(leaveOrIll.endDate) >= startOfDay) &&
                                    user
                                ) {
                                    this.blockedMap.set(this.makeKey(startOfDay, user.id), {
                                        description: leaveOrIll.description,
                                        icon: leaveOrIll.icon,
                                        startDate: leaveOrIll.startDate,
                                        endDate: leaveOrIll.endDate,
                                    });
                                    this.afasAbsenceUserMap.set(this.makeKey(startOfDay, user.id), true);
                                }
                            });
                            loopDate.setDate(loopDate.getDate() + 1);
                        }

                        this.blockedMap = new Map(this.blockedMap);
                        this.refreshPlanningView();
                    })
                );
            }
            this.projects = projects;
            this.planningList = planningList;
            this.planningHass = planningHas;
            this.userPlannings = userPlanning;
            this.projectDayPerformerChangesToSave.forEach(change => {
                this.applyQueuedProjectDayPerformerValue(
                    change.userPlannings,
                    change.nextPerformerId,
                    change.planningHass,
                    change.rowKey,
                    userPlanning,
                    planningList,
                    planningHas
                );
            });
            this.parseData(planningList, projects, planningHas, userPlanning, this.usersMap, entitiesMap);

            this.dataLoadInProgress = false;
            requestAnimationFrame(() => this.setupPlanningPointerOverListener());
            requestAnimationFrame(() => document.body.appendChild(document.createElement('readyforpuppeteer')));
        };

        // Coalesce websocket bursts once, after all latest values have been combined.
        this.periodSubscriptions.add(
            combineLatest([
                planningHas$,
                userPlanning$,
                planning$,
                project$,
                planningUsers$,
                rolesMap$,
                entities$,
                entityTypes$,
            ])
                .pipe(debounceTime(100))
                .subscribe(handleData)
        );
    }

    showInfoDialog() {
        const planningList = this.planningList;
        const planningHas = this.planningHass.filter(
            p =>
                p.driver_user_id === this.contextUser?.id &&
                Utils.formatDate(p.begindate) === Utils.formatDate(this.contextDay)
        );
        const userPlanning = this.userPlannings.filter(
            p =>
                p.user_id === this.contextUser?.id &&
                Utils.formatDate(p.begindate) === Utils.formatDate(this.contextDay)
        );
        const leave = this.leaveDayUserMap.get(this.makeKey(this.contextDay, this.contextUser?.id));
        const afasLeave = this.blockedMap.get(this.makeKey(this.contextDay, this.contextUser?.id));
        this.dialog.open(EmployeeDayInfoDialogComponent, {
            minWidth: '320px',
            panelClass: 'comment-edit-dialog',
            data: {
                planningHas,
                userPlanning,
                leaves: [leave].filter(Boolean),
                afasLeave,
                projects: this.projects,
                planningList,
                user: this.contextUser,
                day: this.contextDay,
            },
        });
    }

    private leaveToMap(leave: Leave, users: User[]) {
        const leaveDates = this.leaveService.generateLeaveDates(leave);
        const userIds = leave.users.map(u => u.id);

        users
            .filter(u => (leave.common ? true : userIds.includes(u.id)))
            .forEach(user => {
                leaveDates.forEach(dates => {
                    const dateString = Utils.formatDate(dates.beginDate);
                    if (!leave.excluded.find(e => e.user_id === user.id && Utils.formatDate(e.date) === dateString)) {
                        this.leaveDayUserMap.set(this.makeKey(dates.beginDate, user.id), leave);
                    }
                });
            });
    }

    private parseData(
        planningList: Planning[],
        projects: Project[],
        planningHass: PlanningHasEntity[],
        userPlannings: UserPlanning[],
        usersMap: Map<number, User>,
        entitiesMap: Map<number, Entity>
    ) {
        userPlannings = (userPlannings ?? []).filter(userPlanning => !userPlanning.deleted_at);

        if (!this.projectDaysList) {
            const newProjects = [
                ...new Set(
                    // @ts-ignore
                    [...projects.map(p => p.afas_project_id), ...planningList.map(p => p.afas_project_id)] || []
                ),
            ];
            this.projectDaysList =
                this.projectDaysList?.filter(
                    p => p.project?.afas_project_id && !newProjects.includes(p.project?.afas_project_id)
                ) || [];
        }

        const currentDateUserDayMap = new Map<string, boolean>();
        const currentDateEntityDayMap = new Map<string, boolean>();
        this.userDatesMap.clear();
        this.dayUserMap.clear();

        const currentDateTeamMap = new Map<string, boolean>();
        planningList.forEach(planning => {
            const date = new Date(Utils.mainPlanning(planning).begindate);
            const projectId = planning.afas_project_id ?? planning.worknumber;
            const teamKey = this.makeKey(date, planning.mainPlanning?.entity_id, projectId);
            currentDateTeamMap.set(teamKey, true);
            this.projectDayTeamMap.set(teamKey, planning);

            planning.performer_user = usersMap.get(planning.performer);
            planning.projectmanager_user = usersMap.get(planning.projectmanager);

            Utils.planningAllEntities(planning).forEach(plnEntity => {
                plnEntity.entity = entitiesMap.get(plnEntity.entity_id);
            });
        });

        this.projectDayTeamMap.forEach((planning, key) => {
            if (!currentDateTeamMap.has(key) && !this.planningsToSave.includes(planning)) {
                this.projectDayTeamMap.delete(key);
            }
        });
        // Build projectDaysList rows first so userPlannings can find the right rowKey
        this.uniqueProjectsIds = [
            ...new Set([
                ...projects.map(project => project.afas_project_id),
                ...planningList.map(planning => planning.afas_project_id ?? planning.worknumber),
                ...planningHass.map(planningHas => this.resolveProjectId(planningHas, planningList)),
                ...userPlannings.map(userPlanning => this.resolveProjectId(userPlanning, planningList)),
            ]),
        ]
            .filter((projectId): projectId is string => !!projectId)
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

        this.uniqueProjectsIds.forEach(projectId => {
            const project = projects.find(p => p.afas_project_id === projectId);
            if (project) {
                const projectUserPlannings = userPlannings.filter(
                    up => this.resolveProjectId(up, planningList) === projectId && !up.deleted_at
                );
                const projectPlanningHas = planningHass.filter(
                    planningHas =>
                        this.resolveProjectId(planningHas, planningList) === projectId && !planningHas.deleted_at
                );
                const projectPlanningHasWithDriver = projectPlanningHas.filter(
                    planningHas => !!planningHas.driver_user_id
                );
                const hasPersonnelPlanning =
                    projectUserPlannings.some(userPlanning => !!userPlanning.user_id) ||
                    projectPlanningHasWithDriver.length > 0 ||
                    planningList.some(
                        planning =>
                            (planning.afas_project_id ?? planning.worknumber) === projectId &&
                            planning.user_planning?.some(
                                userPlanning => !userPlanning.deleted_at && userPlanning.user_id
                            )
                    );
                const baseProjectDay = this.projectDaysList.find(p => p.projectId === projectId);

                // Before the initial filter every project needs a row so it can be checked for planning data.
                // Afterwards an absent row is recreated only when websocket data contains personnel planning for it.
                if (!baseProjectDay && (!this.initialProjectDaysFiltered || hasPersonnelPlanning)) {
                    this.projectDaysList.push(this.createProjectDays(projectId, projects, planningList));
                } else if (baseProjectDay) {
                    if (!baseProjectDay.form.dirty) {
                        baseProjectDay.form.patchValue(project, { emitEvent: false });
                        baseProjectDay.syncedPerformerId = project.performer_id;
                    }
                }

                // Create extra rows for each distinct performer_id found in user_plannings for this project
                // so existing data can be mapped back into separate visible columns.
                const projectPlanningHasPerformerIds = projectPlanningHasWithDriver
                    .map(planningHas => this.resolvePlanningHasPerformerId(planningHas, projectId, userPlannings))
                    .filter((performerId): performerId is number => performerId != null);
                const extraPerformerIds = [
                    ...new Set([
                        ...projectUserPlannings
                            .filter(up => up.performer_id != null)
                            .map(up => up.performer_id as number),
                        ...projectPlanningHasPerformerIds,
                    ]),
                ];
                extraPerformerIds.forEach(performerId => {
                    const matchingRows = this.projectDaysList.filter(
                        p =>
                            p.project?.afas_project_id === projectId &&
                            p.form.controls.performer_id.value === performerId
                    );
                    if (!matchingRows.length) {
                        const variantRow = this.createProjectDays(projectId, projects, planningList);
                        variantRow.form.patchValue({ ...project, performer_id: performerId }, { emitEvent: false });
                        variantRow.syncedPerformerId = performerId;
                        const projectRows = this.projectDaysList.filter(
                            projectDay => projectDay.projectId === projectId
                        );
                        const insertAfterRow = projectRows[projectRows.length - 1];
                        const insertIndex = insertAfterRow
                            ? this.projectDaysList.indexOf(insertAfterRow) + 1
                            : this.projectDaysList.length;

                        this.projectDaysList.splice(insertIndex, 0, variantRow);
                    }
                });
            } else {
                // Realtime data can emit synchronously, so take(1) avoids self-unsubscribing before assignment.
                this.projectService.getSingle(projectId).pipe(take(1)).subscribe();
            }
        });

        planningHass.forEach(planningHas => {
            const afasProjectId =
                planningHas.afas_project_id ??
                planningList.find(p => p.id === planningHas.planning_id)?.afas_project_id ??
                planningList.find(p => p.id === planningHas.planning_id)?.worknumber;
            const performerId = this.resolvePlanningHasPerformerId(planningHas, afasProjectId, userPlannings);
            if (planningHas.performer_id == null && performerId != null) {
                planningHas.performer_id = performerId;
            }
            this.handlePlanningHas(planningHas, afasProjectId, currentDateUserDayMap, currentDateEntityDayMap);
        });

        userPlannings.forEach(userPlanning => {
            const planning = planningList.find(p => p.id === userPlanning.planning_id);
            const afasProjectId = this.resolveProjectId(userPlanning, planningList);
            const date = new Date(userPlanning.begindate);
            // performer_id determines which project row/column a planning belongs to.
            // When it is absent we fall back to the base project row.
            const projectDay =
                userPlanning.performer_id != null
                    ? this.getProjectDayForPerformer(
                          afasProjectId,
                          userPlanning.performer_id,
                          !userPlanning.planning_id
                      )
                    : this.projectDaysList.find(pd => pd.projectId === afasProjectId);
            const mapKey = projectDay?.rowKey ?? afasProjectId;
            const key = this.makeKey(date, userPlanning.user_id, mapKey);

            let current = this.projectDayUserMap.get(key);
            if (!current) {
                current = {
                    type: userPlanning.planning_id ? 'userPlanning' : 'project',
                    planningHas: [],
                    userPlanning: [],
                };
            }
            if (
                !current.userPlanning.some(up => up.id === userPlanning.id) &&
                !this.userPlanningsToSave.some(ups => ups.id === userPlanning.id)
            ) {
                current.userPlanning.push(userPlanning);
            } else {
                const cup = current.userPlanning.find(up => up.id === userPlanning.id);
                if (cup && cup !== userPlanning) {
                    delete userPlanning.deleted_at;
                    Object.assign(cup, userPlanning);
                }
                const ups = this.userPlanningsToSave.find(upts => upts.id === userPlanning.id);
                if (ups && ups !== userPlanning) {
                    delete userPlanning.deleted_at;
                    Object.assign(ups, userPlanning);
                }
            }
            if (current.planningHas?.length || current.userPlanning?.length) {
                this.dayUserMap.set(this.makeKey(date, userPlanning.user_id), afasProjectId);
                this.projectDayUserMap.set(key, current);
            }
            currentDateUserDayMap.set(key, true);
            this.userDatesMap.set(userPlanning.user_id, [
                ...(this.userDatesMap.get(userPlanning.user_id) || []),
                Utils.formatDate(date),
            ]);

            if (planning && userPlanning.planning_id) {
                if (!planning.user_planning.find(up => up.id === userPlanning.id)) {
                    planning.user_planning.push(userPlanning);
                }
            }
        });

        this.planningList?.forEach(planning => {
            planning.user_planning = planning.user_planning.filter(up => !!userPlannings.find(ups => ups.id === up.id));
        });

        this.projectDayUserMap.forEach((projectDayUser, key) => {
            if (!currentDateUserDayMap.has(key)) {
                if (projectDayUser.userPlanning && this.projectDayUserMap.has(key)) {
                    const hasUnsavedOrPending = projectDayUser.userPlanning.some(
                        up => this.userPlanningsToSave.indexOf(up) !== -1 || (!up.id && this.userPlannings.includes(up))
                    );
                    if (!hasUnsavedOrPending) {
                        this.projectDayUserMap.delete(key);
                    } else {
                        this.dayUserMap.set(
                            this.makeKey(
                                projectDayUser.userPlanning[0].begindate,
                                projectDayUser.userPlanning[0].user_id
                            ),
                            this.resolveProjectId(projectDayUser.userPlanning[0])
                        );
                    }
                }
                if (projectDayUser.planningHas && this.projectDayUserMap.has(key)) {
                    if (!projectDayUser.planningHas.some(up => this.planningHasToSave.indexOf(up) !== -1)) {
                        this.projectDayUserMap.delete(key);
                    } else {
                        this.dayUserMap.set(
                            this.makeKey(
                                projectDayUser.planningHas[0].begindate,
                                projectDayUser.planningHas[0].driver_user_id
                            ),
                            this.resolveProjectId(projectDayUser.planningHas[0])
                        );
                    }
                }
            }

            if (
                projectDayUser.userPlanning?.some(
                    m => !!this.userPlanningsToSave.find(up => up.id === m.id)?.deleted_at
                )
            ) {
                this.projectDayUserMap.delete(key);
            }
        });

        this.projectDayMaterialMap.forEach((projectDayMaterial, key) => {
            if (!currentDateEntityDayMap.has(key)) {
                if (
                    this.projectDayMaterialMap.has(key) &&
                    !projectDayMaterial.some(pdm => this.planningHasToSave.indexOf(pdm) !== -1)
                ) {
                    this.projectDayMaterialMap.delete(key);
                }
            }
            if (projectDayMaterial.some(m => !!this.planningHasToSave.find(up => up.id === m.id)?.deleted_at)) {
                this.projectDayMaterialMap.delete(key);
            }
        });

        const currentDates = [];
        const loopDate = new Date(this.fromDate);
        while (loopDate.getTime() < this.toDate.getTime()) {
            currentDates.push(Utils.formatDate(loopDate));
            loopDate.setDate(loopDate.getDate() + 1);
        }

        this.userDatesMap.forEach((dates, userId) => {
            const datesUnique = [...new Set([...dates])].filter(d => currentDates.includes(d));
            if (datesUnique.length !== this.userWeekCount.get(userId)) {
                this.userWeekCount.set(userId, datesUnique.length);
            }
        });

        this.refreshPlanningView();
        this.markEmployeePlanningUserRowsForCheck();
    }

    private generateCounts() {
        if (!this.users?.length || !this.leaveDays?.length) {
            return;
        }

        this.userWeekCount.clear();
        this.users.forEach(user => {
            this.leaveDays.forEach(({ day }) => {
                if (
                    this.dayUserMap.has(this.makeKey(day, user.id)) ||
                    this.leaveDayUserMap.has(this.makeKey(day, user.id)) ||
                    this.afasAbsenceUserMap.has(this.makeKey(day, user.id))
                ) {
                    let count = this.userWeekCount.get(user.id) ?? 0;

                    count++;

                    this.userWeekCount.set(user.id, count);
                }
            });
        });

        this.userDayHasNightMap.clear();
        this.userDayConflictMap.clear();
        const userDayPlanningWindows = new Map<string, Array<{ start: Date; end: Date; isNight: boolean }>>();
        const addPlanningWindow = (begindate: Date, enddate: Date, userId: number): void => {
            const userDayKey = this.makeKey(Utils.newDate(begindate), userId);
            const planningWindow = {
                start: new Date(begindate),
                end: new Date(enddate),
                isNight: Utils.isNight(begindate, enddate),
            };

            if (planningWindow.isNight) {
                this.userDayHasNightMap.set(userDayKey, true);
            }

            const planningWindows = userDayPlanningWindows.get(userDayKey) ?? [];
            planningWindows.push(planningWindow);
            userDayPlanningWindows.set(userDayKey, planningWindows);
        };

        this.projectDayUserMap.forEach(projectDayUser => {
            projectDayUser.userPlanning?.forEach(({ begindate, enddate, user_id, deleted_at }) => {
                if (!deleted_at) {
                    addPlanningWindow(begindate, enddate, user_id);
                }
            });

            projectDayUser.planningHas?.forEach(({ begindate, enddate, driver_user_id, deleted_at }) => {
                if (!deleted_at) {
                    addPlanningWindow(begindate, enddate, driver_user_id);
                }
            });
        });

        userDayPlanningWindows.forEach((planningWindows, userDayKey) => {
            if (planningWindows.length > 1) {
                this.userDayConflictMap.set(userDayKey, true);
            }
        });
    }

    private handlePlanningHas(
        planningHas: PlanningHasEntity,
        afasProjectId: string,
        currentDateUserDayMap: Map<string, boolean>,
        currentDateEntityDayMap: Map<string, boolean>
    ) {
        if (!planningHas.entity_id) {
            return;
        }
        const date = new Date(planningHas.begindate);
        if (
            !this.entitiesMap.get(planningHas.entity_id)?.use_once &&
            [EntityId.HiringEmployee, EntityId.HiringMaterials].indexOf(planningHas.entity_id) !== -1
        ) {
            if (!this.entityHiringNames.has(planningHas.entity_id)) {
                this.entityHiringNames.set(planningHas.entity_id, []);
            }
            if (!this.entityHiringNames.get(planningHas.entity_id).find(z => z === planningHas.name)) {
                this.entityHiringNames.get(planningHas.entity_id).push(planningHas.name);
            }
        }

        const performerId = this.resolvePlanningHasPerformerId(planningHas, afasProjectId);
        if (planningHas.performer_id == null && performerId != null) {
            planningHas.performer_id = performerId;
        }

        if (planningHas.driver_user_id) {
            const userMapKey =
                this.getProjectDayForPerformer(afasProjectId, planningHas.performer_id, !planningHas.planning_id)
                    ?.rowKey ?? afasProjectId;
            const key = this.makeKey(date, planningHas.driver_user_id, userMapKey);
            let current = this.projectDayUserMap.get(key);
            if (!current) {
                current = {
                    type: planningHas.planning_id ? 'planning' : 'project',
                    planningHas: [],
                    userPlanning: [],
                };
            }
            if (!current.planningHas.some(phe => phe.id === planningHas.id)) {
                current.planningHas.push(planningHas);
            }
            this.dayUserMap.set(this.makeKey(date, planningHas.driver_user_id), afasProjectId);
            this.projectDayUserMap.set(key, current);
            currentDateUserDayMap.set(key, true);
        }

        // Do for all material, because auto couple of entities
        const nonUseOnceName = this.entitiesMap.get(planningHas.entity_id)?.use_once ? null : planningHas.name;
        const materialKey = this.getProjectDayMaterialMapKeys(date, planningHas.entity_id, {
            projectId: afasProjectId,
            nonUseOnceName,
            performerId: planningHas.performer_id,
            preferLastMatchingRow: !planningHas.planning_id,
        })[0];

        const currentMaterial = materialKey ? (this.projectDayMaterialMap.get(materialKey) ?? []) : [];
        if (materialKey && !currentMaterial.some(phe => +phe.id === +planningHas.id)) {
            currentMaterial.push(planningHas);
            this.projectDayMaterialMap.set(materialKey, currentMaterial);
        }
        if (materialKey) {
            currentDateEntityDayMap.set(materialKey, true);
        }

        const days = this.entityWeekUsedDays.get(nonUseOnceName + planningHas.entity_id) ?? [];
        const dateString = Utils.formatDate(planningHas.begindate);
        if (!days.find(d => d === dateString)) {
            days.push(dateString);
        }
        this.entityWeekUsedDays.set(nonUseOnceName + planningHas.entity_id, days);

        this.userDatesMap.set(planningHas.driver_user_id, [
            ...(this.userDatesMap.get(planningHas.driver_user_id) || []),
            Utils.formatDate(date),
        ]);
    }

    private createProjectDays(afasProjectId: string, projects: Project[], planningList?: Planning[]): ProjectDay {
        const planning =
            planningList?.find(p => (p.afas_project_id ?? p.worknumber) === afasProjectId && !p.is_transport) ??
            planningList?.find(p => (p.afas_project_id ?? p.worknumber) === afasProjectId);
        const project = projects.find(p => p.afas_project_id === afasProjectId) ?? planning?.project;
        const projectDays: ProjectDay = {
            project,
            projectId: afasProjectId,
            rowKey: `${afasProjectId}::${this.projectDayRowCounter++}`,
            syncedPerformerId: project?.performer_id,
            worknumber: planning?.worknumber ?? project?.afas_project_id,
            form: new FormGroup<
                ControlsOf<{
                    location: string;
                    location_housenumber: string;
                    location_place: string;
                    location_postcode: string;
                    location_straat: string;
                    performer_id: number;
                    projectmanager_id: number;
                }>
            >({
                location: new FormControl(project?.location),
                location_housenumber: new FormControl(project?.location_housenumber),
                location_place: new FormControl(project?.location_place),
                location_postcode: new FormControl(project?.location_postcode),
                location_straat: new FormControl(project?.location_straat),
                performer_id: new FormControl(project?.performer_id),
                projectmanager_id: new FormControl(project?.projectmanager_id),
            }),
            days: [] as ProjectDay['days'],
        };
        if (!project?.afas_project_id) {
            projectDays.form.disable();
        }
        this.periodSubscriptions.add(
            projectDays.form.dirty$.subscribe(dirty => {
                this.formDirty = dirty;
            })
        );
        // When performer_id changes, the projectDay may need to move to a different row (the one matching the new performer_id, or the base row if no performer_id).
        this.periodSubscriptions.add(
            projectDays.form.controls.performer_id.valueChanges.subscribe(() => {
                if (this.rollingBack) {
                    return;
                }

                const previousPerformerId = projectDays.syncedPerformerId ?? null;
                const nextPerformerId = projectDays.form.controls.performer_id.value ?? null;
                const existingChangeIndex = this.projectDayPerformerChangesToSave.findIndex(
                    change => change.rowKey === projectDays.rowKey
                );
                const existingChange =
                    existingChangeIndex > -1 ? this.projectDayPerformerChangesToSave[existingChangeIndex] : null;
                const { userPlannings: queuedUserPlannings, planningHass: queuedPlanningHass } =
                    this.buildProjectDayPerformerChangePayload(projectDays, previousPerformerId, existingChange);

                this.applyQueuedProjectDayPerformerValue(
                    queuedUserPlannings,
                    nextPerformerId,
                    queuedPlanningHass,
                    projectDays.rowKey
                );

                if (previousPerformerId === nextPerformerId) {
                    if (existingChangeIndex > -1) {
                        this.projectDayPerformerChangesToSave.splice(existingChangeIndex, 1);
                    }
                    this.refreshPlanningView();
                    this.markEmployeePlanningUserRowsForCheck();
                    return;
                }

                // Variants can exist without linked user_planning rows (for example material-only rows).
                // We still queue the row-level performer change so undo can restore the previous selection.
                const change: ProjectDayPerformerChange = {
                    rowKey: projectDays.rowKey,
                    previousPerformerId,
                    nextPerformerId,
                    userPlannings: queuedUserPlannings,
                    planningHass: queuedPlanningHass,
                };

                if (existingChangeIndex > -1) {
                    this.projectDayPerformerChangesToSave.splice(existingChangeIndex, 1, change);
                } else {
                    this.projectDayPerformerChangesToSave.push(change);
                }
                this.refreshPlanningView();
                this.markEmployeePlanningUserRowsForCheck();
            })
        );

        this.leaveDays.forEach(({ day, dayLetter, dayKey, dayLabel, cssClass }) => {
            projectDays.days.push({ day, project, dayLetter, dayKey, dayLabel, cssClass });
        });
        return projectDays;
    }

    addHiringFromInput(): void {
        const value = `${this.fcAddHiring.value ?? ''}`.trim();
        if (!value || !this.addingHiringForEntityId) {
            return;
        }
        this.addHiring(value);
    }

    addHiring(teamName: string) {
        const newHiring = `${teamName ?? ''}`.trim();
        if (!newHiring || !this.addingHiringForEntityId) {
            return;
        }
        const array = this.entityHiringNames.get(this.addingHiringForEntityId) ?? [];
        if (array.indexOf(newHiring) === -1) {
            array.push(newHiring);
            this.entityHiringNames.set(this.addingHiringForEntityId, array);
        }
        this.fcAddHiring.reset();
        this.addingHiringForEntityId = null;
    }

    startAddHiring(entity: Entity) {
        if (this.readonly) {
            return;
        }

        this.addingHiringForEntityId = entity.id;
        requestAnimationFrame(() => {
            this.addHiringInput.nativeElement.focus();
        });
    }

    pdf() {
        this.downloadingPdf = true;
        const filename = `Personeelsplanning-week_${this.week}-${this.year}.pdf`;
        this.subscriptions.add(
            this.planningService.generateEmployeePdf(this.week, this.year).subscribe({
                next: (data: Blob) => {
                    saveAs(data, filename);

                    this.downloadingPdf = false;
                },
                error: () => {
                    this.downloadingPdf = false;
                },
            })
        );
    }

    removeHire(hireEntityId: number, user: string) {
        if (this.entityWeekUsedDays.get(user + hireEntityId).length > 0) {
            this.confirmDialogService.confirm({
                title: 'Verwijderen niet mogelijk',
                text: `Verwijderen is niet mogelijk omdat er nog inhuur ingepland is. Verwijder eerst de inhuur.`,
                confirm: 'Sluiten',
            });
        } else {
            const items = this.entityHiringNames.get(hireEntityId);
            items.splice(items.indexOf(user), 1);
            this.entityHiringNames.set(hireEntityId, items);
        }
    }

    setHourType(hourtype: RealisationHourtype) {
        this.currentHourType = hourtype;
        this.addOrRemoveLeave(this.contextUser, this.contextDay, true, true);
    }

    addOrRemoveLeave(user: User, day: Date, setHourType?: boolean, changeProject?: boolean) {
        const dateString = Utils.formatDate(day);
        const checkHourtype = this.leaveDayUserMap.get(this.makeKey(day, user.id))?.hourtype ?? this.currentHourType;
        const leaves = this.leaveData.filter(
            l => l.hourtype === checkHourtype && dateString === Utils.formatDate(l.begindate) && !l.deleted_at
        );

        let leave = leaves[0];
        if (leaves.length > 1) {
            leave = leaves.find(l => !!l.users.find(u => u.id === user.id));
        }
        if (this.currentHourType === RealisationHourtype.sleep) {
            leave = this.leaveData.find(
                l =>
                    l.hourtype === checkHourtype &&
                    dateString === Utils.formatDate(l.begindate) &&
                    !l.deleted_at &&
                    l.afas_project_id === this.contextProject?.afas_project_id
            );
        }

        if (leave && leave?.interval_week) {
            this.confirmDialog
                .confirm({
                    title: 'Herhalend verlof',
                    text: `Herhalend verlof kan alleen via de verlofmodule worden aangepast.`,
                    confirm: 'Sluiten',
                    cancel: 'Naar verlof detail',
                })
                .catch(() => this.router.navigateByUrl(`/leave/${leave.id}`));
            return;
        }

        if (!this.leaveDayUserMap.has(this.makeKey(day, user.id)) && leave?.comment !== this.contextComment) {
            leave = null;
        }
        if (
            (this.currentHourType === RealisationHourtype.sleep &&
                leave?.afas_project_id !== this.contextProject?.afas_project_id) ||
            changeProject
        ) {
            leave = null;
        }

        const leaveUsers = () => {
            if (!leave.users) {
                leave.users = [];
            }
            let actionIsAdd = false;

            if (leave.excluded.find(e => e.user_id === user.id)) {
                leave.excluded.splice(
                    leave.excluded.findIndex(e => e.user_id === user.id),
                    1
                );
                actionIsAdd = true;
            } else {
                if (leave?.common) {
                    leave.excluded.push({ user_id: user.id, date: day });
                    this.leaveDayUserMap.delete(this.makeKey(day, user.id));
                } else {
                    if (leave.users.find(u => u.id === user.id)) {
                        leave.users.splice(
                            leave.users.findIndex(u => u.id === user.id),
                            1
                        );
                        this.leaveDayUserMap.delete(this.makeKey(day, user.id));
                        actionIsAdd = true;
                    } else {
                        leave.users.push(user);
                    }
                }
            }

            if (!this.leaveData.includes(leave)) {
                this.leaveData.push(leave);
            }
            if (!this.leavesToSave.includes(leave)) {
                this.leavesToSave.push(leave);
            }
            this.leaveToMap(leave, this.users);
            this.refreshPlanningView();
            // Context-menu and dialog callbacks run outside the row event that normally marks an OnPush row dirty.
            this.markEmployeePlanningUserRowsForCheck([user.id]);
            return actionIsAdd;
        };

        if (!leave) {
            if (!this.currentHourType) {
                return;
            }
            const dialogRef = this.dialog.open<AddLeaveNameDialogComponent, any, returnData>(
                AddLeaveNameDialogComponent,
                {
                    width: '320px',
                    panelClass: 'comment-edit-dialog',
                    data: {
                        currentHourType: this.currentHourType,
                        contextComment: this.contextComment,
                        contextProject: this.contextProject,
                        projects: this.projectDaysList.map(pd => pd.project),
                    },
                }
            );
            const subs = dialogRef.afterClosed().subscribe((data: { comment: string; project: Project }) => {
                if (data) {
                    this.contextProject = data.project;
                    this.contextComment = data.comment;
                    leave = new Leave();
                    leave.hourtype = this.currentHourType;
                    if (leave.hourtype === RealisationHourtype.sleep) {
                        leave.afas_project_id = this.contextProject.afas_project_id;
                    }
                    const beginDate = new Date(day);
                    Utils.setTime(beginDate, Settings.DEFAULT_START, 0, 0);
                    const endDate = new Date(beginDate);
                    Utils.setTime(endDate, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);
                    leave.begindate = beginDate;
                    leave.enddate = endDate;
                    const begintime = new Date();
                    Utils.setTime(begintime, Settings.DEFAULT_START, 0, 0);
                    const endtime = new Date();
                    Utils.setTime(endtime, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);
                    leave.comment = data.comment;
                    leave.begintime = Utils.formatDate(begintime, 'HH:mm');
                    leave.endtime = Utils.formatDate(endtime, 'HH:mm');
                    leaveUsers();
                }
                subs.unsubscribe();
            });
        } else {
            if (leaveUsers() && setHourType) {
                this.addOrRemoveLeave(this.contextUser, this.contextDay);
            }
        }
    }

    showProjectDetails(project: Project) {
        const executor = this.executors?.find(e => e.id === project.performer_id);
        const projectManager = this.projectManagers?.find(pm => pm.id === project.projectmanager_id);
        this.dialog.open(ProjectDetailDialogComponent, {
            width: '99vw',
            maxWidth: '400px',
            panelClass: 'project-detail-dialog',
            data: {
                project,
                executor,
                projectManager,
            },
        });
    }

    private getUserPlanningVisibility(): { projectIds: Set<string>; rowKeys: Set<string> } {
        const projectIds = new Set<string>();
        const rowKeys = new Set<string>();
        const selectedRoleKeys = this.fcRoleKeys.value ?? [];
        // Role filters should hide projects that are only occupied by filtered-out users.
        const visibleUserIds = !selectedRoleKeys.length
            ? new Set((this.users ?? []).map(user => user.id).filter((id): id is number => id !== undefined))
            : new Set(
                  (this.uniqueRoles ?? [])
                      .filter(role => selectedRoleKeys.includes(role.key))
                      .flatMap(role => role.users.map(user => user.id).filter((id): id is number => id !== undefined))
              );

        // Include only planning items that have actual user planning (personeelsplanning)
        this.planningList?.forEach(planning => {
            const projectId = planning.afas_project_id ?? planning.worknumber;
            if (
                projectId &&
                planning.user_planning?.some(up => !up.deleted_at && up.user_id && visibleUserIds.has(up.user_id))
            ) {
                projectIds.add(projectId);
            }
        });

        const planningProjectIdMap = new Map(
            this.planningList?.map(planning => [planning.id, planning.afas_project_id ?? planning.worknumber])
        );

        this.projectDayUserMap.forEach((projectDayUser, key) => {
            const hasVisibleUserPlanning = projectDayUser.userPlanning?.some(
                userPlanning =>
                    !userPlanning.deleted_at && !!userPlanning.user_id && visibleUserIds.has(userPlanning.user_id)
            );
            const hasVisibleDriverPlanning = projectDayUser.planningHas?.some(
                planningHas =>
                    !planningHas.deleted_at &&
                    !!planningHas.driver_user_id &&
                    visibleUserIds.has(planningHas.driver_user_id)
            );

            if (hasVisibleUserPlanning || hasVisibleDriverPlanning) {
                const projectDay = (this.projectDaysList ?? []).find(row => key.endsWith(`-${row.rowKey}`));
                if (projectDay) {
                    rowKeys.add(projectDay.rowKey);
                }
            }

            projectDayUser.userPlanning?.forEach(userPlanning => {
                if (userPlanning.deleted_at || !userPlanning.user_id || !visibleUserIds.has(userPlanning.user_id)) {
                    return;
                }

                const projectId =
                    this.resolveProjectId(userPlanning) ?? planningProjectIdMap.get(userPlanning.planning_id);

                if (projectId) {
                    projectIds.add(projectId);
                }
            });

            projectDayUser.planningHas?.forEach(planningHas => {
                if (
                    planningHas.deleted_at ||
                    !planningHas.driver_user_id ||
                    !visibleUserIds.has(planningHas.driver_user_id)
                ) {
                    return;
                }

                const projectId =
                    this.resolveProjectId(planningHas) ?? planningProjectIdMap.get(planningHas.planning_id);

                if (projectId) {
                    projectIds.add(projectId);
                }
            });
        });

        return { projectIds, rowKeys };
    }

    private refreshPlanningView(): void {
        this.generateCounts();
        this.filterProjectDaysList();
    }

    private markEmployeePlanningUserRowsForCheck(userIds?: Array<number | undefined>): void {
        // The maps are intentionally mutated in place. Mark only affected OnPush rows so their cells update
        // without making every employee row render again after one planning or leave change.
        const requestedUserIds = userIds ? new Set(userIds) : null;
        this.employeePlanningUserRows?.forEach(row => {
            if (!requestedUserIds || requestedUserIds.has(row.user().id)) {
                row.markForCheck();
            }
        });
    }

    /**
     * Merges rows that ended up on the same project and performer after the save completed.
     */
    private mergeDuplicatePerformerProjectRows(): void {
        const projectIds = [...new Set((this.projectDaysList ?? []).map(projectDay => projectDay.projectId))];

        projectIds.forEach(projectId => {
            const performerIds = [
                ...new Set(
                    (this.projectDaysList ?? [])
                        .filter(projectDay => projectDay.projectId === projectId)
                        .map(projectDay => projectDay.form.controls.performer_id.value)
                        .filter((performerId): performerId is number => performerId != null)
                ),
            ];

            performerIds.forEach(performerId => {
                const matchingRows = (this.projectDaysList ?? []).filter(
                    projectDay =>
                        projectDay.projectId === projectId &&
                        projectDay.form.controls.performer_id.value === performerId
                );

                if (matchingRows.length < 2) {
                    return;
                }

                const keeper = matchingRows[0];
                matchingRows
                    .filter(projectDay => projectDay !== keeper)
                    .forEach(projectDay => this.mergeProjectDayRowInto(projectDay, keeper));
            });
        });
    }

    /**
     * Moves all row-keyed state into the matching main row and removes the duplicate.
     */
    private mergeProjectDayRowInto(source: ProjectDay, target: ProjectDay): void {
        this.remapProjectDayUserMap(source.rowKey, target.rowKey);
        this.remapProjectDayMaterialMap(source.rowKey, target.rowKey);
        this.remapQueuedProjectDayPerformerChanges(source.rowKey, target.rowKey);

        const sourceIndex = this.projectDaysList.indexOf(source);
        if (sourceIndex !== -1) {
            this.projectDaysList.splice(sourceIndex, 1);
        }
    }

    /**
     * Rewrites user-row map entries from one visible row key to another and merges collisions on the target row.
     */
    private remapProjectDayUserMap(sourceRowKey: string, targetRowKey: string): void {
        [...this.projectDayUserMap.entries()].forEach(([key, state]) => {
            if (!key.endsWith(`-${sourceRowKey}`)) {
                return;
            }

            const targetKey = `${key.slice(0, key.length - sourceRowKey.length)}${targetRowKey}`;
            const targetState = this.projectDayUserMap.get(targetKey);

            this.projectDayUserMap.set(targetKey, this.mergeProjectDayUserState(state, targetState));
            this.projectDayUserMap.delete(key);
        });
    }

    /**
     * Combines two row states so the target row keeps all user planning and planning-has entries.
     */
    private mergeProjectDayUserState(
        sourceState: ProjectDayUserState,
        targetState?: ProjectDayUserState
    ): ProjectDayUserState {
        if (!targetState) {
            return sourceState;
        }

        return {
            type: targetState.type === 'project' ? sourceState.type : targetState.type,
            userPlanning: this.mergeUniqueItems(
                targetState.userPlanning ?? [],
                sourceState.userPlanning ?? [],
                (left, right) => this.sameEntityOrSavedId(left, right)
            ),
            planningHas: this.mergeUniqueItems(
                targetState.planningHas ?? [],
                sourceState.planningHas ?? [],
                (left, right) => this.sameEntityOrSavedId(left, right)
            ),
        };
    }

    /**
     * Rewrites material-row map entries from one visible row key to another and merges duplicate references by id.
     */
    private remapProjectDayMaterialMap(sourceRowKey: string, targetRowKey: string): void {
        [...this.projectDayMaterialMap.entries()].forEach(([key, planningHass]) => {
            if (!key.includes(`-${sourceRowKey}`)) {
                return;
            }

            const targetKey = key.replace(`-${sourceRowKey}`, `-${targetRowKey}`);
            this.projectDayMaterialMap.set(
                targetKey,
                this.mergeUniqueItems(this.projectDayMaterialMap.get(targetKey) ?? [], planningHass, (left, right) =>
                    this.sameEntityOrSavedId(left, right)
                )
            );
            this.projectDayMaterialMap.delete(key);
        });
    }

    /**
     * Repoints queued performer changes to the surviving row and merges duplicate queued payloads when needed.
     */
    private remapQueuedProjectDayPerformerChanges(sourceRowKey: string, targetRowKey: string): void {
        const sourceChanges = this.projectDayPerformerChangesToSave.filter(change => change.rowKey === sourceRowKey);

        sourceChanges.forEach(change => {
            const targetChange = this.projectDayPerformerChangesToSave.find(
                existingChange => existingChange.rowKey === targetRowKey
            );

            if (!targetChange) {
                change.rowKey = targetRowKey;
                return;
            }

            targetChange.userPlannings = this.mergeQueuedUserPlannings(
                targetChange.userPlannings,
                change.userPlannings
            );
            targetChange.planningHass = this.mergeQueuedPlanningHass(targetChange.planningHass, change.planningHass);
            this.projectDayPerformerChangesToSave.splice(this.projectDayPerformerChangesToSave.indexOf(change), 1);
        });
    }

    /**
     * Merges queued user-planning descriptors while keeping unsaved rows matchable by their identifying fields.
     */
    private mergeQueuedUserPlannings(
        left: QueuedProjectDayPerformerUserPlanning[] = [],
        right: QueuedProjectDayPerformerUserPlanning[] = []
    ): QueuedProjectDayPerformerUserPlanning[] {
        return this.mergeUniqueItems(left, right, (existingUserPlanning, userPlanning) =>
            this.isSameQueuedProjectDayPerformerUserPlanning(existingUserPlanning, userPlanning)
        );
    }

    /**
     * Merges queued planning-has descriptors while keeping unsaved rows matchable by their identifying fields.
     */
    private mergeQueuedPlanningHass(
        left: QueuedProjectDayPerformerPlanningHas[] = [],
        right: QueuedProjectDayPerformerPlanningHas[] = []
    ): QueuedProjectDayPerformerPlanningHas[] {
        return this.mergeUniqueItems(left, right, (existingPlanningHas, planningHas) =>
            this.isSameQueuedProjectDayPerformerPlanningHas(existingPlanningHas, planningHas)
        );
    }

    /**
     * Register one user_planning mutation in the save queue and mirror it into the visible project-day maps.
     * Both team-planning and direct project-planning create the same in-memory bookkeeping once the candidate
     * UserPlanning has been constructed, so that shared part lives here.
     */
    private storeUserPlanningOnProjectDay(
        userPlanning: UserPlanning,
        day: { day: Date; project?: Project },
        userId: number,
        type: ProjectDayUserState['type'],
        rowKey?: string,
        planning?: Planning
    ): void {
        const existingUserPlanning = this.findActiveExistingUserPlanning(userPlanning);
        const nextUserPlanning = existingUserPlanning ?? userPlanning;

        if (existingUserPlanning) {
            const queuedDelete =
                existingUserPlanning.id &&
                this.userPlanningsToSave.find(item => item.id === existingUserPlanning.id && !!item.deleted_at);
            if (queuedDelete) {
                this.userPlanningsToSave.splice(this.userPlanningsToSave.indexOf(queuedDelete), 1);
            }
        } else if (
            !this.userPlanningsToSave.some(
                item => this.isSameUserPlanningTimeSlot(item, userPlanning) && !item.deleted_at
            )
        ) {
            this.userPlanningsToSave.push(userPlanning);
        }

        this.normalizeUserPlanningsToSave();
        this.dayUserMap.set(this.makeKey(day.day, userId), day.project?.afas_project_id);
        this.projectDayUserMap.set(this.makeKey(day.day, userId, rowKey ?? day.project?.afas_project_id), {
            type,
            userPlanning: [nextUserPlanning],
        });

        if (planning) {
            if (!planning.user_planning) {
                planning.user_planning = [];
            }

            if (!planning.user_planning.includes(nextUserPlanning)) {
                planning.user_planning.push(nextUserPlanning);
            }
        }
    }

    /**
     * Checks whether the user already has another planning on the same day.
     * This is the cross-row conflict guard: it scans all visible row-keyed entries,
     * while still allowing a click on the exact same cell to behave as a toggle/remove action.
     */
    private userHasPlanningOnDay(
        day: Date,
        userId: number,
        projectId?: string,
        rowKey?: string,
        candidateStart?: Date,
        candidateEnd?: Date
    ): boolean {
        const requestedKey = this.makeKey(day, userId, rowKey ?? projectId);

        // The same visible cell is allowed here because a click on it is handled as a remove/toggle action.
        if (this.projectDayUserMap.has(requestedKey)) {
            return false;
        }

        const dayKey = Utils.formatDate(day);
        const candidateIsNight = candidateStart && candidateEnd ? Utils.isNight(candidateStart, candidateEnd) : null;

        // Conflict detection is no longer a simple same-day lookup:
        // the same user can now exist in multiple row-keyed cells, so we scan all visible entries.
        const hasPlanningOnDay = Array.from(this.projectDayUserMap.entries()).some(([mapKey, projectDayUser]) => {
            const userPlanning = projectDayUser.userPlanning?.find(existingUserPlanning => {
                if (existingUserPlanning.deleted_at || existingUserPlanning.user_id !== userId) {
                    return false;
                }

                const sameDay = Utils.formatDate(existingUserPlanning.begindate) === dayKey;

                if (
                    candidateStart &&
                    candidateEnd &&
                    this.timeRangesOverlap(
                        existingUserPlanning.begindate,
                        existingUserPlanning.enddate,
                        candidateStart,
                        candidateEnd
                    )
                ) {
                    return true;
                }

                if (
                    projectId &&
                    sameDay &&
                    this.resolveProjectId(existingUserPlanning) === projectId &&
                    candidateIsNight !== null
                ) {
                    const existingIsNight = Utils.isNight(existingUserPlanning.begindate, existingUserPlanning.enddate);
                    return existingIsNight === candidateIsNight;
                }

                return !candidateStart || !candidateEnd ? sameDay : false;
            });

            const planningHas = projectDayUser.planningHas?.find(existingPlanningHas => {
                if (existingPlanningHas.deleted_at || existingPlanningHas.driver_user_id !== userId) {
                    return false;
                }

                const sameDay = Utils.formatDate(existingPlanningHas.begindate) === dayKey;

                if (
                    candidateStart &&
                    candidateEnd &&
                    this.timeRangesOverlap(
                        existingPlanningHas.begindate,
                        existingPlanningHas.enddate,
                        candidateStart,
                        candidateEnd
                    )
                ) {
                    return true;
                }

                if (
                    projectId &&
                    sameDay &&
                    this.resolveProjectId(existingPlanningHas) === projectId &&
                    candidateIsNight !== null
                ) {
                    const existingIsNight = Utils.isNight(existingPlanningHas.begindate, existingPlanningHas.enddate);
                    return existingIsNight === candidateIsNight;
                }

                return !candidateStart || !candidateEnd ? sameDay : false;
            });

            return !!userPlanning || !!planningHas;
        });

        return hasPlanningOnDay;
    }

    private timeRangesOverlap(startA: Date, endA: Date, startB: Date, endB: Date): boolean {
        return (
            new Date(startA).getTime() < new Date(endB).getTime() &&
            new Date(endA).getTime() > new Date(startB).getTime()
        );
    }

    /**
     * Determine whether a planning overlaps the 00:00-03:00 window.
     * That is the branch-specific rule used to classify linked asphalt planning as "night".
     */
    private worksBetweenMidnightAndThree(startDate: Date, endDate: Date): boolean {
        const currentDay = new Date(startDate);
        currentDay.setHours(0, 0, 0, 0);

        while (currentDay < endDate) {
            const nightWindowStart = new Date(currentDay);
            const nightWindowEnd = new Date(currentDay);
            nightWindowEnd.setHours(3, 0, 0, 0);

            // Treat any overlap with 00:00-03:00 as a night planning, even when the shift started the evening before.
            if (startDate < nightWindowEnd && endDate > nightWindowStart) {
                return true;
            }

            currentDay.setDate(currentDay.getDate() + 1);
        }

        return false;
    }

    private getLinkedPlanningForProjectDay(day: Date, projectDay?: ProjectDay): Planning | undefined {
        if (!projectDay?.projectId || !this.projectDayMatchesConfiguredPerformer(projectDay)) {
            return undefined;
        }

        // Only the configured performer row is allowed to mirror asphalt-planning times.
        // Manual extra rows keep their own schedule so parallel plannings can diverge safely.
        const dayKey = Utils.formatDate(day);
        const linkedPlanning = this.planningList?.find(planning => {
            const planningProjectId = planning.afas_project_id ?? planning.worknumber;
            return (
                planningProjectId === projectDay.projectId &&
                Utils.formatDate(Utils.mainPlanning(planning).begindate) === dayKey
            );
        });

        if (!linkedPlanning) {
            return undefined;
        }

        const planningStartDate = new Date(Utils.mainPlanning(linkedPlanning).begindate);
        const planningEndDate = new Date(Utils.mainPlanning(linkedPlanning).enddate);
        const planningIsNight = this.worksBetweenMidnightAndThree(planningStartDate, planningEndDate);

        // Only copy asphalt-planning times when the selected row belongs to that performer
        // and the linked planning matches the current day/night mode in the UI.
        return planningIsNight === !!this.fcNightPlanning.value ? linkedPlanning : undefined;
    }

    /**
     * Get the default performers for the project
     * @param projectDay
     * @returns
     */
    projectDayMatchesConfiguredPerformer(projectDay: ProjectDay | undefined): boolean {
        const selectedPerformerId = projectDay?.form.controls.performer_id.value;
        const project = this.projects?.find(p => p.afas_project_id === projectDay?.projectId);
        const performerIds = [project?.asphalt_performer_id, project?.performer_id].filter(Boolean);

        return performerIds.includes(selectedPerformerId);
    }

    private resolveProjectId(
        planningItem?: { afas_project_id?: string; planning?: Planning; planning_id?: number },
        planningList: Planning[] = this.planningList
    ): string | undefined {
        // Project ids can come from the item itself, its linked planning or the fetched planning list.
        // Centralising that fallback keeps the row-mapping logic consistent in one place.
        const planning = planningList?.find(p => p.id === planningItem?.planning_id);
        return (
            planningItem?.afas_project_id ??
            planningItem?.planning?.afas_project_id ??
            planningItem?.planning?.worknumber ??
            planning?.afas_project_id ??
            planning?.worknumber
        );
    }

    /**
     * Resolves the exact map entry for the currently addressed visible row/cell.
     * It prefers the row-specific key and only falls back to the plain project key
     * so older/base entries remain reachable during the row-key migration.
     */
    private getProjectDayEntry<T>(
        map: Map<string, T>,
        day: Date,
        entityOrUserId: number,
        projectId?: string,
        rowKey?: string
    ): T | undefined {
        // During the transition from project-based keys to row-based keys both variants can exist.
        // Check rowKey first, then fall back to projectId for older/base entries.
        return [...new Set([rowKey, projectId].filter((identifier): identifier is string => !!identifier))]
            .map(identifier => map.get(this.makeKey(day, entityOrUserId, identifier)))
            .find(item => !!item);
    }

    /**
     * Finds the actual map key that belongs to a specific user-planning state.
     * This is used when removing or undoing a planning, because the clicked row key
     * is not always the same key under which the existing state was originally stored.
     */
    private findProjectDayUserMapKey(
        day: Date,
        userId: number,
        projectId?: string,
        rowKey?: string,
        state?: ProjectDayUserState
    ): string | undefined {
        const exactKey = [rowKey, projectId]
            .filter((identifier): identifier is string => !!identifier)
            .map(identifier => this.makeKey(day, userId, identifier))
            .find(key => this.projectDayUserMap.get(key) === state);

        if (exactKey) {
            return exactKey;
        }

        const dayUserPrefix = `${this.makeKey(day, userId)}-`;
        return [...this.projectDayUserMap.entries()].find(
            ([key, value]) => key.startsWith(dayUserPrefix) && value === state
        )?.[0];
    }

    onDateSelected(event: any) {
        this.openChangesBackActionCheck().then(result => {
            if (result) {
                this.dataLoadInProgress = true;
                this.clearMaps();
                const date = new Date(event.value);
                this.router.navigate(['personeelsplanning', Utils.getWeekNumber(date), date.getFullYear()]);
            }
        });
    }
}

export type ProjectDayUserState = {
    type: 'userPlanning' | 'planning' | 'project';
    userPlanning?: UserPlanning[];
    planningHas?: PlanningHasEntity[];
};

type QueuedProjectDayPerformerUserPlanning = {
    id?: number;
    user_id: number;
    begindate: Date;
    enddate: Date;
    planning_id?: number;
    afas_project_id?: string;
};

type QueuedProjectDayPerformerPlanningHas = {
    id?: number;
    entity_id: number;
    begindate: Date;
    enddate: Date;
    planning_id?: number;
    afas_project_id?: string;
    name?: string;
};

type ProjectDayPerformerChange = {
    rowKey: string;
    previousPerformerId: number | null;
    nextPerformerId: number | null;
    userPlannings: QueuedProjectDayPerformerUserPlanning[];
    planningHass: QueuedProjectDayPerformerPlanningHas[];
};

export interface ProjectDay {
    project?: Project;
    projectId: string;
    // Stable key for one visible project row.
    // Multiple rows can exist for the same projectId, so map lookups cannot key only by project anymore.
    rowKey: string;
    /**
     * Last performer value that has already been propagated into the linked user_planning rows.
     * This is used as the sync baseline.
     */
    syncedPerformerId?: number;
    worknumber?: string;
    form: FormGroup<
        ControlsOf<{
            location: string;
            location_housenumber: string;
            location_place: string;
            location_postcode: string;
            location_straat: string;
            performer_id: number;
            projectmanager_id: number;
        }>
    >;
    days: {
        day: Date;
        project?: Project;
        dayLetter: string;
        dayKey: string;
        dayLabel: string;
        cssClass: string;
    }[];
}
